// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CurrentLang, Resources } from '@shared/localizations/Resources';
import { brokerMessageScreenHandler, messageBoxHandler, riskWarningMessageScreenHandler } from '@shared/utils/AppHandlers';
import { ArrayUtils } from '@shared/utils/ArrayUtils';
import { AssetTradingMode } from '@shared/utils/Asset/AssetConst';
import { AccountTradeStatus, AccountType } from '@shared/utils/cache/Accounts/Account.types';
import { FINISH_BLOCK_TRANSFER } from '@shared/utils/CommonConst';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { CustomInstrumentListTypeEnum } from '@shared/utils/CustomInstrumentList/CustomInstrumentListTypeEnum';
import { DirectInstrumentMessage, DirectReportMessage, DirectRiskWarningMessage, Message } from '@shared/utils/DirectMessages/DirectMessagesImport';
import { CloseAccountResponseStatus, DirectPropertyOwner, SubscribeType } from '@shared/utils/Enums/Constants';
import { EventSource, EventType } from '@shared/utils/Event/EventConstants.types';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { Periods } from '@shared/utils/History/TFInfo';
import { InstrDateSettings } from '@shared/utils/Instruments/InstrDateSettings';
import { InstrumentSpecificType } from '@shared/utils/Instruments/InstrumentSpecificType';
import { InstrumentTypes } from '@shared/utils/Instruments/InstrumentTypes';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { InteriorIdUtils } from '@shared/utils/Instruments/InteriorIdUtils';
import { OptionContract } from '@shared/utils/Instruments/OptionContract';
import { OptionPutCall } from '@shared/utils/Instruments/OptionPutCall';
import { OptionTradingStyle } from '@shared/utils/Instruments/OptionTradingStyle';
import { PriceFormatter } from '@shared/utils/Instruments/PriceFormatter';
import { PriceLimitMeasure } from '@shared/utils/Instruments/PriceLimitMeasure';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { TradingMode } from '@shared/utils/Instruments/TradingMode';
import { VariableTick } from '@shared/utils/Instruments/VariableTick';
import { ReportManager } from '@shared/utils/Managers/ReportManager';
import { MathUtils } from '@shared/utils/MathUtils';
import { BrokerMessageResponseType } from '@shared/utils/Notifications/BrokerMessageResponseType';
import { NotificationsCounterChangedEvent } from '@shared/utils/Notifications/NotificationsCounterChangedEvent';
import { TextMessageType } from '@shared/utils/Notifications/TextMessageType';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { TradeSessionStatus } from '@shared/utils/Session/TradeSessionStatus';
import { delay } from '@shared/utils/Time/DateTimeUtils';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderStatus } from '@shared/utils/Trading/OrderStatus';
import { PlacedFrom } from '@shared/utils/Trading/PlacedFrom';
import { StrategySubscriptionStatus } from '@shared/utils/TradingIdeas/StrategySubscriptionStatus';
import { TradingLockUtils } from '@shared/utils/TradingLockUtils';
import { MarginRequestMode } from '@shared/vendors/Interfaces/IMarginRequestParameters';
import { BusinessRejectMessage } from '@shared/vendors/PFSVendor/WebFix/Messages/BusinessRejectMessage';
import { AccountFollowerService } from './AccountFollowerService';
import { Account } from './cache/Account';
import { AlertManager } from './cache/AlertManager';
import { HeatmapCache } from './cache/Algorithm/Heatmap/HeatmapCache';
import { AlgorithmCache } from './cache/AlgorithmCache';
import { AllowedReport } from './cache/AllowedReport';
import { AccessType } from './cache/AllowedReportConstants';
import { Asset, AssetType } from './cache/Asset';
import { CommissionPlan } from './cache/Commissions/CommissionPlan';
import { CountryCache } from './cache/CountryCache';
import { CrossRateCache } from './cache/CrossRateCache';
import { CrossratesPlans } from './cache/CrossratesPlans';
import { CustodialPlans } from './cache/CustodialPlans';
import { CustomInstrumentListsCache } from './cache/CustomInstrumentListsCache';
import { DataCacheHolder } from './cache/DataCacheHolder';
import { EntitlementManager } from './cache/Entitlement/EntitlementManager';
import { Event } from './cache/Event/Event';
import { EventCache } from './cache/Event/EventCache';
import { EventDescriptionItem } from './cache/Event/EventDescriptionItem';
import { ExternalLinksCache } from './cache/ExternalLinksCache';
import { FundingRateMarkupPlans } from './cache/FundingRateMarkupPlans';
import { GoogleAuthManager } from './cache/GoogleAuthManager';
import { HistoricalBM } from './cache/HistoricalBM';
import { Instrument } from './cache/Instrument';
import { InstrumentGroup } from './cache/InstrumentGroup';
import { InterestPlan } from './cache/InterestPlan';
import { MamGroupsCache } from './cache/MamGroupsCache';
import { NewsCache } from './cache/NewsCache';
import { NewTrade } from './cache/NewTrade';
import { OptionsCache, OptionsFilter } from './cache/OptionsCache';
import { OrderHistoryCache } from './cache/OrderHistoryCache';
import { OrderParamsContainer } from './cache/OrderParams/OrderParamsContainer';
import { Position } from './cache/Position';
import { QuoteCache } from './cache/QuoteCache';
import { RiskPlan } from './cache/RiskPlan';
import { HistoryDepthLimit } from './cache/RuleProperties/HistoryDepthLimits';
import { RulesCache } from './cache/RulesCache';
import { SessionsCache } from './cache/SessionsCache';
import { SourceCollection } from './cache/SourceCollection';
import { SpreadPlan } from './cache/SpreadPlan';
import { ThirdPartyProvidersValidator } from './cache/ThirdPartyProvidersValidator';
import { TradingSystem } from './cache/TradingSystem';
import { User } from './cache/User';
import { Connection } from './Connection';
import { ErrorInformationStorage } from './ErrorInformationStorage';
import { InstrumentBalancer, UserBalancer, UserBalancesAssetBalanceFields } from './InstrumentBalancer';
import { IsAllowed } from './IsAllowed';
import { LocalStorage } from './LocalStorage';
import { MarginCounterLocal } from './MarginCounterLocal';
import { Routes } from './Routes';
import { BaseSettings } from './Settings/BaseGeneralSettingsWrapper';
import { Trading } from './Trading';
import { PortfolioCache } from '../Portfolio/PortfolioCache';
import { OrderExecutor } from './Trading/OrderExecutor';
import { BaseSettingsUtils } from './UtilsClasses/BaseGeneralSettingsUtilsWrapper';
export class _DataCache {
    constructor() {
        // #region Private
        this._instrumentsByIdRequests = new Map();
        this._optionsByIdRequests = new Map();
        this._instrumentsByTradabldIdRequests = new Map();
        // #endregion
        // Events
        this.OnAddAccount = new CustomEvent();
        this.OnUpdateAccount = new CustomEvent();
        this.OnRemoveAccount = new CustomEvent();
        this.OnAddInstrument = new CustomEvent();
        this.OnUpdateInstrument = new CustomEvent();
        this.OnAddPosition = new CustomEvent();
        this.OnUpdatePosition = new CustomEvent();
        this.OnRemovePosition = new CustomEvent();
        this.OnPositionExerciseStatusChanged = new CustomEvent();
        // CorporateAction
        this.OnAddCorporateAction = new CustomEvent();
        this.OnUpdateCorporateAction = new CustomEvent();
        this.OnRemoveCorporateAction = new CustomEvent();
        this.OnAddOrder = new CustomEvent();
        this.OnUpdateOrder = new CustomEvent();
        this.OnRemoveOrder = new CustomEvent();
        this.OnActivateOrder = new CustomEvent();
        this.OnAddSLOrderToPosition = new CustomEvent();
        this.OnRemoveSLOrderFromPosition = new CustomEvent();
        this.OnAddTPOrderToPosition = new CustomEvent();
        this.OnRemoveTPOrderFromPosition = new CustomEvent();
        this.OnUpdateAssetEvent = new CustomEvent();
        this.OnAddNewTradeEvent = new CustomEvent();
        this.OnUpdateTradeEvent = new CustomEvent();
        this.OnRemovedTradeEvent = new CustomEvent();
        // TODO. Rename if necessary.
        this.OnReportMessage = new CustomEvent();
        this.OnRiskRuleWarningMessage = new CustomEvent();
        this.OnMessageForTicketViewer = new CustomEvent();
        this.AddOrderHistoryEvent = new CustomEvent();
        this.RemovedOrderHistoryEvent = new CustomEvent();
        this.filledOrdersArray = [];
        this.StatementArray = [];
        this.MamSummaryArray = [];
        this.IgnoreTradeSession = false;
        this.realTimeFilledOrdersArray = [];
        this.ExistingInstrumentTypes = [];
        this.reportDict = {};
        this.Instruments = {};
        this.InstrumentsByInteriorIDCache = {};
        this.InstrumentContinuousContractsByInteriorIDCache = {};
        this.InstrumentsArray = [];
        this.InstrumentsByTradableIdCache = {};
        this.InstrumentsTableByIDs = {};
        this.InstrumentsContinuousContractIDS = {};
        this.InstrumentsByTradingSessionsId = {}; // {ID:[...]}
        this.InstrumentsBySourceRouteIdTradableId = {}; // список инструментов для которых инструмент с TradableId:RouteId является DataSource инструментом
        this.Accounts = {};
        this._ownedAccounts = {}; // user's owned accounts
        this.SessionCache = new SessionsCache();
        this.Loaded = false;
        // +++++ нужно поправить
        this.FAllowedHistoryTypes = [HistoryType.BID, HistoryType.LAST, HistoryType.ASK];
        this.CrossRateCache = new CrossRateCache(this);
        this.BrendingRules = null;
        this.assetCollection = {};
        // chart place order default settings
        this.TIF = [];
        this.TIF_Limit_And_Stop_Limit = [];
        this.TIF_Stop = [];
        // chart place order default settings
        // TODO. cleanup.
        this.UserLogin = '';
        this.NonFixedList = true;
        this.OrderParameterContainer = new OrderParamsContainer();
        /// <summary>
        /// Кеш истории
        /// </summary>
        this.FOrderHistoryCache = new OrderHistoryCache(this);
        /// <summary>
        /// Кеш котировок
        /// </summary>
        this.FQuoteCache = new QuoteCache(this);
        this.Routes = new Routes();
        this.Trading = new Trading(this);
        this.newTradesDict = {};
        this.Exchanges = {};
        this.TradingExchanges = [];
        this.tradeSessionStatusCache = {};
        this.riskPlanDict = {};
        this.lastRiskPlanItem = null;
        this.instrTypes = {};
        this.deferredMessages = {};
        this.deferredInsReq = {};
        //
        this.OnReintialize = new CustomEvent();
        this.instrNames = {};
        this.InstrumentBalancerTable = {};
        // public userBalanserTable : any = {};
        this.baseCurrency = 'USD'; // валюта сервера
        this.userBaseCurrency = 'USD'; // валюта юзера
        this._userHasSameCurrencyOnAllAccounts = null;
        this.LoadedResolve = null;
        this.EmptyUserBalancer = new UserBalancer(null);
        // TODO?
        this.ContractsCache = {};
        this._cachedInsListByContractID = {};
        this.InstrumentsByIdCache = new Map();
        this.SpreadPlans = {};
        this.SpreadPlan = null;
        this.FLogin = null;
        this.LoginUser = null;
        this.AggregatedInstruments = {};
        // public cachedInstrumentsWR : any = null;
        this.AccountFollowerService = new AccountFollowerService(this);
        this.PortfolioCache = new PortfolioCache(this);
        this.AlgorithmCache = new AlgorithmCache(this);
        this.HeatmapCache = new HeatmapCache(this);
        this.OptionsCache = new OptionsCache(this);
        this.customInstrumentListsCache = new CustomInstrumentListsCache();
        this.CommissionPlans = {};
        this.OnCommissionPlanUpdate = new CustomEvent();
        this.CrossratesPlans = new CrossratesPlans(this); // #101199
        this.InterestsOnBalancePlans = new Map(); // #128273
        this.RulesCache = new RulesCache(this);
        this.CompanyName = null;
        this.CompanyURL = null;
        this.BrokerInformation = null;
        this.PoweredByName = null;
        this.PoweredByURL = null;
        this.BrandingVisibleInstrumentLogo = false;
        this.AllowPreviewPassword = false;
        this.ExternalLinksCache = new ExternalLinksCache();
        this.NewsCache = new NewsCache(this);
        this.tradingSystems = {};
        this.OnTradingSystemsChange = new CustomEvent();
        this.OnTradingSystemUnsubscribe = new CustomEvent();
        this.OnProcessBrokerMessage = new CustomEvent();
        this.OnBMCounterMsgReceived = new CustomEvent();
        this.CountryCache = new CountryCache();
        this.AlertManager = new AlertManager(this);
        this.EntitlementManager = new EntitlementManager(this);
        this.GoogleAuthManager = new GoogleAuthManager();
        this.CustodialPlans = new CustodialPlans();
        this.ActiveUser = null; // TODO без понятия что с этим делать
        this._userGroups = {};
        this.Users = new Map();
        this.FundingRateMarkupPlans = new FundingRateMarkupPlans();
        this.OnTradingSessionStatusUpdated = new CustomEvent();
        this.productTypeEnabilityCache = {};
        this.SourceCollection = new SourceCollection();
        this.OnRecalculatedBalances = new CustomEvent();
        // TODO  MainAccountNew
        this.MainAccountNew = null;
        this.closeAccountApproved = false;
        this.ManualControlPriceTStopOrders = false;
        this.closeAccountRequestID = null;
        this.OnChangeCloseAccountRequestID = new CustomEvent();
        this._userHasSameCommissionAndSwapPlanOnAllAccounts = null;
        this._delayedOptionsByUnderlierIdRouteId = new Map();
        this.OnHistoryChangedAndNeedReload = new CustomEvent();
        this.OnAccountFollowerResponse = new CustomEvent();
        this.ThirdPartyProvidersValidator = new ThirdPartyProvidersValidator();
        this.createGetInstrumentByIdPromise = (instrumentID) => __awaiter(this, void 0, void 0, function* () {
            const msg = yield Connection.vendor.getInstrumentById(instrumentID);
            return this._gIBId(msg);
        });
        this.createGetOptionByIdPromise = (optionID) => __awaiter(this, void 0, void 0, function* () {
            const msgArr = yield Connection.vendor.getInstrumentById(optionID);
            if (msgArr.length === 0) {
                return false;
            }
            const msg = msgArr[0];
            if (msg.Code !== Message.CODE_INSTRUMENT) {
                return false;
            }
            const instrumentMessage = msg;
            const forwardBaseInstrument = this.GetInstrumentsByInstrumentId(instrumentMessage.ForwardBaseInstrumentID.toString());
            if (!isValidArray(forwardBaseInstrument)) {
                yield this.getInstrumentById(instrumentMessage.ForwardBaseInstrumentID);
            }
            this.NewMessage(msg);
            return true;
        });
        this.createGetInstrumentByTradableIdPromise = (tradableId, routeId, interiorId, routeValidationIgnore) => __awaiter(this, void 0, void 0, function* () {
            const msg = yield Connection.vendor.getInstrumentByInstrumentTradableID_NFL(tradableId);
            if (isNullOrUndefined(msg)) {
                return null;
            }
            if (!msg.length) {
                return null;
            }
            if (msg[0].Code !== Message.CODE_INSTRUMENT) {
                return null;
            }
            const instrMessage = msg[0];
            if (instrMessage.InstrType === InstrumentTypes.OPTIONS) {
                yield this.requestUnderlierIfNeeded(instrMessage.ForwardBaseInstrumentID);
            }
            for (let i = 0; i < msg.length; i++) {
                if (msg[i].Code === Message.CODE_INSTRUMENT) {
                    this.NewMessage(msg[i]);
                }
            }
            let ins = null;
            if (!isValidString(interiorId)) {
                ins = this.getInstrumentByTradable_ID(tradableId, routeId);
            }
            else {
                ins = this.getInstrumentByName(interiorId);
            }
            if (isNullOrUndefined(ins)) {
                if (routeValidationIgnore) {
                    ins = this.getInstrumentByTradable_ID_first(tradableId);
                }
                if (isNullOrUndefined(ins)) {
                    return null;
                }
            }
            const rpKeys = Object.keys(this.riskPlanDict);
            for (let i = 0, len = rpKeys.length; i < len; i++) {
                this.riskPlanDict[rpKeys[i]].processOneInstrument(ins, this.riskPlanDict[rpKeys[i]].items);
            }
            return ins;
        });
        this.requestUnderlierIfNeeded = (underlierId) => __awaiter(this, void 0, void 0, function* () {
            if (!isValidNumber(underlierId) || underlierId === -1) {
                return false;
            }
            const instruments = this.GetInstrumentsByInstrumentId(underlierId.toString());
            if (instruments.length > 0) {
                return true;
            }
            return yield this.getInstrumentById(underlierId);
        });
        new IsAllowed(this);
        DataCacheHolder.setDataCache(this);
        ErrorInformationStorage.SetDataCache(this);
        this.FOrderExecutor = new OrderExecutor(this);
        this.FOrderHistoryCache.AddOrderHistoryEvent.Subscribe((orderHistory) => {
            this.AddOrderHistoryEvent.Raise(orderHistory);
        }, this);
        this.FOrderHistoryCache.RemovedOrderHistoryEvent.Subscribe((orderHistory) => {
            this.AddOrderHistoryEvent.Raise(orderHistory);
        }, this);
        RiskPlan.showProductType = false;
    }
    cleanup() {
        // Data start to load
        this.Loaded = false;
        this.CustodialPlans.Clear();
        this.AlertManager.Clear(); // тут должна произойти отписка алертами от котировок, это должно случиться раньше чем будут очищены инструменты иначе не удастся отправить корректный запрос на отписку
        this.EntitlementManager.Clear();
        this.FundingRateMarkupPlans.Clear();
        // lost clean
        this.UserLogin = '';
        this.instrNames = {};
        this.InstrumentsByIdCache.clear();
        this.FLogin = null;
        this.LoginUser = null;
        // reports
        ReportManager.clear();
        this.FOrderHistoryCache.Clear();
        this.filledOrdersArray = [];
        this.StatementArray = [];
        this.MamSummaryArray = [];
        this.realTimeFilledOrdersArray = [];
        this.ExistingInstrumentTypes = [];
        this.reportDict = {};
        this.Trading.clear();
        this.newTradesDict = {};
        this.Instruments = {};
        this.InstrumentsByInteriorIDCache = {};
        this.InstrumentContinuousContractsByInteriorIDCache = {};
        this.InstrumentsByTradableIdCache = {};
        this.InstrumentsArray = [];
        this.InstrumentsTableByIDs = {};
        this.InstrumentsContinuousContractIDS = {};
        this.InstrumentsByTradingSessionsId = {};
        this.InstrumentsBySourceRouteIdTradableId = {};
        this.Accounts = {};
        MamGroupsCache.clear();
        this.SessionCache.clear();
        // +++++ нужно поправить
        this.FAllowedHistoryTypes = [HistoryType.BID, HistoryType.LAST, HistoryType.ASK];
        this.TIF = [];
        this.TIF_Limit_And_Stop_Limit = [];
        this.TIF_Stop = [];
        this.AccountFollowerService.clear();
        this.CrossRateCache.Clear();
        EventCache.Clear();
        this.FQuoteCache.clear();
        this.PortfolioCache.Clear();
        this.AlgorithmCache.clear();
        this.HeatmapCache.Clear();
        this.RulesCache.Clear();
        this.OptionsCache.clear();
        this.customInstrumentListsCache.clear();
        this.Routes.clear();
        this.Exchanges = {};
        this.TradingExchanges.length = 0;
        this.tradeSessionStatusCache = {};
        this.assetCollection = {};
        this.riskPlanDict = {};
        this.lastRiskPlanItem = null;
        RiskPlan.showProductType = false;
        this.deferredMessages = {};
        this.deferredInsReq = {};
        this.instrTypes = {};
        //
        // this.OnReintialize = new CustomEvent();
        this.InstrumentBalancerTable = {};
        // this.userBalanserTable = {};
        this.ContractsCache = {};
        this._cachedInsListByContractID = {};
        this.BrendingRules = null;
        this.SpreadPlans = {};
        this.SpreadPlan = null;
        this.AggregatedInstruments = {};
        // this.cachedInstrumentsWR = null;
        this.CommissionPlans = {};
        this.CrossratesPlans.Clear();
        this.InterestsOnBalancePlans.clear();
        this.CompanyName = null;
        this.CompanyURL = null;
        this.BrokerInformation = null;
        this.PoweredByName = null;
        this.PoweredByURL = null;
        this.BrandingVisibleInstrumentLogo = false;
        this.AllowPreviewPassword = false;
        this.ExternalLinksCache.clear();
        this.NewsCache.clear();
        this.tradingSystems = {};
        this._userGroups = {};
        this.Users.clear();
        this.productTypeEnabilityCache = {};
        this._userHasSameCurrencyOnAllAccounts = null;
        this._userHasSameCommissionAndSwapPlanOnAllAccounts = null;
        this.closeAccountRequestID = null;
        this.closeAccountApproved = false;
        this._delayedOptionsByUnderlierIdRouteId.clear();
    }
    handleRouteMessageDirect(message) {
        const isNewRoute = this.Routes.addRoute(message);
        const route = this.Routes.getRouteById(message.RouteId);
        if (!route) {
            return;
        }
        if (isNewRoute) {
            if (route.IsInfo) {
                this.updateInstrumentsForInfoRoute(route);
            }
            if (route.IsTradable) {
                this.updateInstrumentsForTradeRoute(route);
            }
        }
        if (route.IsNewsRoute) {
            this.NewsCache.addRoute(message);
        }
    }
    updateInstrumentsForTradeRoute(route) {
        const instrumentsByRouteId = this.InstrumentsTableByIDs[route.RouteId];
        if (!instrumentsByRouteId) {
            return;
        }
        const instrumentKeys = Object.keys(instrumentsByRouteId);
        for (const key of instrumentKeys) {
            instrumentsByRouteId[key].RouteName = route.Name;
        }
    }
    updateInstrumentsForInfoRoute(route) {
        const routeId = route.RouteId;
        const instrumentsByRouteId = this.InstrumentsTableByIDs[routeId];
        if (!instrumentsByRouteId) {
            return;
        }
        const instrumentKeys = Object.keys(instrumentsByRouteId);
        for (const key of instrumentKeys) {
            const instrument = instrumentsByRouteId[key];
            const mappedInstrument = this.getInstrument(instrument.ShortName, routeId);
            if (mappedInstrument === null) {
                continue;
            }
            instrument.InstrumentDayBarMessageUpdateMode = route.InstrumentDayBarMessageUpdateMode;
            mappedInstrument.InstrumentDayBarMessageUpdateMode = route.InstrumentDayBarMessageUpdateMode;
        }
    }
    /// +++++++
    getInstrumentByName(name) {
        if (!isValidString(name)) {
            return null;
        }
        let filter = name.toUpperCase();
        const ans = this.Instruments[filter];
        if (!isNullOrUndefined(ans)) {
            return ans;
        }
        // For ContinuousContract searching
        const splited = filter.split('|');
        if (splited.length > 1) {
            filter = splited[0];
            const answear = this.Instruments[filter];
            if (answear) {
                return answear;
            }
            const splitedFilter = filter.split(InstrumentUtils.SEPARATOR);
            if (splitedFilter.length > 1) {
                const Id = Number(splited[1]);
                const Route = Number(splitedFilter[1]);
                return this.getInstrumentByInstrumentIdAndRoute(Id, Route);
            }
        }
        return null;
    }
    getInstrumentByInteriorIdAsync(interiorID) {
        return __awaiter(this, void 0, void 0, function* () {
            const instrument = this.getInstrumentByName(interiorID);
            if (!isNullOrUndefined(instrument)) {
                return instrument;
            }
            if (!isValidString(interiorID)) {
                return null;
            }
            if (!this.NonFixedList) {
                return null;
            }
            const { name, route } = InstrumentUtils.DivideFullName(interiorID);
            if (!isValidString(name) || !isValidString(route)) {
                return null;
            }
            const Id = Number(name);
            const Route = Number(route);
            return yield this.getInstrumentByInstrumentTradableID_NFL(Id, Route);
        });
    }
    getInstrumentByInstrumentIdAndRoute(Id, Route) {
        let tmp = this.InstrumentsContinuousContractIDS[Id];
        if (!tmp) {
            return null;
        }
        tmp = tmp[Route];
        if (!tmp) {
            return null;
        }
        return tmp.CurrentContinuousContract;
    }
    GetInstrumentsByDisplayName(pattern) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!isValidString(pattern)) {
                return [];
            }
            if (this.NonFixedList) {
                return yield this.getFilteredNonFixedList(pattern);
            }
            else {
                return this.getFilteredFixedList(pattern);
            }
        });
    }
    normalizeFilterNames(filter) {
        return filter
            .toUpperCase()
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean);
    }
    filterInstrumentsByNames(instruments, names) {
        const namesSet = new Set(names);
        return instruments.filter((ins) => {
            if (isNullOrUndefined(ins)) {
                return false;
            }
            return namesSet.has(ins.DisplayName().toUpperCase());
        });
    }
    getFilteredNonFixedList(filter) {
        return __awaiter(this, void 0, void 0, function* () {
            const aliasLanguage = CurrentLang;
            const instrumentTypes = [
                InstrumentTypes.EQUITIES_CFD,
                InstrumentTypes.FUTURES,
                InstrumentTypes.FOREX,
                InstrumentTypes.INDICIES,
                InstrumentTypes.CRYPTO,
                InstrumentTypes.SPREADBET,
                InstrumentTypes.EQUITIES,
                InstrumentTypes.CFD_FUTURES,
                InstrumentTypes.FIXED_INCOME,
                InstrumentTypes.ETF,
                InstrumentTypes.TBILL,
                InstrumentTypes.SPOT,
                InstrumentTypes.OPTIONS,
                InstrumentTypes.FORWARD,
                InstrumentTypes.CORPORATE,
                InstrumentTypes.FUND
            ];
            const names = this.normalizeFilterNames(filter);
            const instrumentsList = [];
            for (const name of names) {
                const nonFixedList = yield DataCache.GetInstrumentList(InstrumentUtils.RemoveRouteName(name), null, instrumentTypes, false, aliasLanguage, false);
                for (const ins of nonFixedList) {
                    if (!ins.isFakeNonFixed()) {
                        instrumentsList.push(ins);
                        continue;
                    }
                    let existInstruments = this.GetInstrumentsByInstrumentId(ins.Id);
                    if (existInstruments.length === 0) {
                        yield this.getInstrumentById(ins.Id);
                        existInstruments = this.GetInstrumentsByInstrumentId(ins.Id);
                    }
                    instrumentsList.push(...existInstruments);
                }
            }
            return this.filterInstrumentsByNames(instrumentsList, names);
        });
    }
    getFilteredFixedList(filter) {
        const names = this.normalizeFilterNames(filter);
        return this.filterInstrumentsByNames(Object.values(this.Instruments), names);
    }
    // Direct vendor scheme.
    getInstrument(symbol, route) {
        return this.getInstrumentByName(InstrumentUtils.GetFullName(symbol, route));
    }
    getInstrumentByTradable_ID(tradableID, route) {
        const routeGroup = this.InstrumentsTableByIDs[route];
        if (routeGroup) {
            return routeGroup[tradableID] || null;
        }
        else {
            return null;
        }
    }
    getFirstTradeInstrumentByTradableId(tradableID) {
        for (const insK in this.InstrumentsTableByIDs) {
            const route = this.Routes.getRouteByTextId(insK);
            if (route === null || route === void 0 ? void 0 : route.IsTradable) {
                const ins = this.InstrumentsTableByIDs[insK][tradableID];
                if (!isNullOrUndefined(ins)) {
                    return ins;
                }
            }
        }
        return null;
    }
    getInstrumentByTradable_ID_first(tradableID) {
        const tradeIns = this.getFirstTradeInstrumentByTradableId(tradableID);
        if (tradeIns) {
            return tradeIns;
        }
        for (const insK in this.InstrumentsTableByIDs) {
            const route = this.Routes.getRouteByTextId(insK);
            if (route === null || route === void 0 ? void 0 : route.IsInfo) {
                const ins = this.InstrumentsTableByIDs[insK][tradableID];
                if (ins) {
                    return ins;
                }
            }
        }
        return null;
    }
    getCachedInstrumentDict() {
        return this.Instruments;
    }
    getInstrumentsByTradableID(tradableID) {
        if (!tradableID || !this.InstrumentsByTradableIdCache[tradableID]) {
            return [];
        }
        return this.InstrumentsByTradableIdCache[tradableID];
    }
    getAnyInstrument() {
        let interiorID = null;
        for (interiorID in this.Instruments) {
            break;
        }
        return this.Instruments[interiorID];
    }
    getAccounts() {
        return this.Accounts;
    }
    getSortedAccountsArray(onlyOwned = true, onlyActive = true) {
        const accounts = Object.values(onlyOwned ? this.getOwnedAccounts() : this.getAccounts());
        const filteredAccounts = onlyActive
            ? accounts.filter((acc) => acc.AccountTradeStatus === AccountTradeStatus.ACTIVE_STATUS)
            : accounts;
        return filteredAccounts.sort((a, b) => a.FullAccString.localeCompare(b.FullAccString));
    }
    /** Retrieves the accounts owned by the user. @returns {Record<string, Account>} An object containing the user's owned accounts, where the keys are account IDs and the values are corresponding Account objects. */
    getOwnedAccounts() {
        return this._ownedAccounts;
    }
    /** Updates the user's owned accounts based on the current state of all accounts. This method filters the accounts to include only those owned by the user. @private @returns {void} */
    updateOwnedAccounts() {
        this._ownedAccounts = Object.fromEntries(Object.entries(this.Accounts).filter(([, account]) => {
            return account.IsOwnedByUser && this.UserLogin === account.userLogin;
        }));
    }
    /** Retrieves the total number of accounts, including those that available but not owned by the user (e.g., linked accounts). @param {boolean} [recalc=false] - Flag indicating whether to recalculate the count. @returns {number} The total number of accounts. */
    getNumberOfAccounts(recalc = false) {
        if (recalc || this._accountsCount == null) {
            this._accountsCount = Object.values(this.Accounts).length;
        }
        return this._accountsCount;
    }
    getAccountByIdOrFirst(id) {
        const account = this.GetAccountById(id);
        if (account) {
            return account;
        }
        return this.getAccountFirst();
    }
    GetAccountById(id) {
        if (!this.Accounts || !id) {
            return null;
        }
        if (this.Accounts[id]) {
            return this.Accounts[id];
        }
        // TODO remove mb
        const keys = Object.keys(this.Accounts);
        let i;
        const len = keys.length;
        for (i = 0; i < len; i++) {
            const key = keys[i];
            if (key.toUpperCase() === id.toUpperCase()) {
                return this.Accounts[key];
            }
        }
        return null;
    }
    getAccountFirst() {
        const accountKeys = Object.keys(this.Accounts);
        if (accountKeys.length > 0) {
            return this.Accounts[accountKeys[0]];
        }
    }
    GetAccountByIdOrName(fullAccString) {
        if (!this.Accounts || !isValidString(fullAccString)) {
            return null;
        }
        const byId = this.GetAccountById(fullAccString);
        if (!isNullOrUndefined(byId)) {
            return byId;
        }
        const keys = Object.keys(this.Accounts);
        let i;
        const len = keys.length;
        const filter = fullAccString.toUpperCase();
        for (i = 0; i < len; i++) {
            const key = keys[i];
            const acc = this.Accounts[key];
            if (acc.FullAccString.toUpperCase() === filter) {
                return acc;
            }
        }
        return null;
    }
    GetAccountByUserLogin(login) {
        const keys = Object.keys(this.Accounts);
        for (let i = 0; i < keys.length; i++) {
            if (this.Accounts[keys[i]].userLogin === login) {
                return this.Accounts[keys[i]];
            }
        }
        return null;
    }
    GetAccountByNumber(fullAccString) {
        if (!this.Accounts || !fullAccString) {
            return null;
        }
        let acc = this.Accounts[fullAccString];
        if (!acc) {
            acc = null;
        }
        return acc;
    }
    getDefaultInstrument(isOptionMasterMode) {
        const tradingSymbol = BaseSettingsUtils.selectedTradingSymbol;
        if (isNullOrUndefined(tradingSymbol)) {
            return null;
        }
        const symbolID = tradingSymbol.GetInteriorID();
        let defaultInstrument = this.getInstrumentByName(symbolID);
        if (isNullOrUndefined(defaultInstrument)) {
            const k = Object.keys(this.Instruments);
            defaultInstrument = this.Instruments[k[0]];
        }
        if (isOptionMasterMode) {
            let defaultOption;
            if (!isNullOrUndefined(defaultInstrument)) {
                const underlier = defaultInstrument.InstrType === InstrumentTypes.OPTIONS
                    ? defaultInstrument.ForwardBaseInstrument
                    : defaultInstrument;
                defaultOption = this.OptionsCache.findOptionByUnderlier(underlier);
            }
            if (isNullOrUndefined(defaultOption)) {
                defaultOption = this.OptionsCache.getFirstOptionInstrument();
            }
            defaultInstrument = defaultOption;
        }
        return defaultInstrument;
    }
    getDefaultAccount(accountID) {
        let defAcc = this.Accounts[accountID];
        if (isNullOrUndefined(defAcc)) {
            const k = Object.keys(this.Accounts);
            defAcc = this.Accounts[k[0]];
        }
        return defAcc;
    }
    GetAssetById(id) {
        return this.assetCollection[id] || null;
    }
    GetAssetByName(name) {
        const list = Object.keys(this.assetCollection);
        for (let i = 0, len = list.length; i < len; i++) {
            const value = this.assetCollection[list[i]];
            if (name === value.Name) {
                return value;
            }
        }
        return null;
    }
    getAssetDict() {
        return this.assetCollection;
    }
    // #region Direct
    NewMessage(message) {
        switch (message.Code) {
            case Message.CODE_ORDER_HISTORY:
                this.handleOrderHistoryMessage(message);
                break;
            case Message.CODE_OPENORDER:
                this.Trading.HandleOpenOrderMessageDirect(message);
                break;
            case Message.CODE_ACCOUNTSTATUS:
                this.HandleAccountStatusMessageDirect(message);
                break;
            case Message.CODE_PFIX_PROPERTY_MESSAGE:
                this.HandlePropertyMessageDirect(message);
                break;
            case Message.CODE_INSTRUMENT:
                this.HandleInstrumentMessageDirect(message);
                break;
            case Message.CODE_OPENPOSITION:
                this.Trading.HandlePositionMessageDirect(message);
                break;
            case Message.CODE_CLOSEPOSITION:
                this.Trading.HandleClosePositionMessageDirect(message);
                break;
            case Message.CODE_REPLACE_ORDER_MESSAGE:
                this.Trading.TryRemoveOrder(message.OrderId);
                break;
            case Message.CODE_CANCEL_ORDER_MESSAGE:
                this.Trading.HandleCancelOrderMessageDirect(message);
                break;
            case Message.CODE_SESSION_MESSAGE:
                this.HandleSessionMessageDirect(message);
                break;
            case Message.CODE_ASSET_TYPE_MESSAGE:
                this.HandleDirectAssetMessage(message);
                break;
            case Message.CODE_CROSSRATE_MESSAGE:
                this.HandleCrossRateMessageDirect(message);
                break;
            case Message.CODE_ROUTE:
                this.handleRouteMessageDirect(message);
                break;
            case Message.CODE_BUSINESS_REJECT_MESSAGE:
                this.HandleReportMessageDirect(message);
                break;
            case Message.CODE_REPORT_TYPE_MESSAGE:
                this.handleReportTypeMessage(message);
                break;
            case Message.CODE_QUOTE:
            case Message.CODE_QUOTE2:
            case Message.CODE_QUOTE3:
            case Message.CODE_INSTRUMENT_DAY_BAR:
            case Message.CODE_INSTRUMENT_PRICES:
                this.handleQuoteMessage(message);
                break;
            case Message.CODE_TRADE:
                this.AddNewTrade(message);
                break;
            case Message.CODE_MODIFY_TRADE_MESSAGE:
                this.UpdateTrade(message);
                break;
            case Message.CODE_YTM_RESPONSE:
                this.handleYTMMessage(message);
                break;
            case Message.CODE_PFIX_RISK_PLAN_MESSAGE:
                this.addRiskPlan(message);
                break;
            case Message.CODE_TRADE_SESSION_STATUS_MESSAGE:
                this.processTradeSessionStatusMessage(message);
                break;
            case Message.CODE_CROSSRATES_PLAN_MESSAGE:
                this.ProcessCrossratesPlanMessage(message);
                break;
            case Message.CODE_INSTRUMENT_TYPE:
                this.AddInstrumentType(message);
                break;
            case Message.CODE_SPREAD_PLAN_MESSAGE:
                this.AddSpreadPlan(message);
                break;
            case Message.CODE_PFIX_TRADE_INSTRUMENT:
                this.ProcessInstrumentUpdateMessage(message);
                break;
            case Message.CODE_PFIX_TRADE_SESSION_SWITCH:
                this.ProcessTradeSessionSwitchMessage(message);
                break;
            case Message.CODE_TEXT_MESSAGE:
                this.ProcessBrokerMessage(message);
                break;
            case Message.CODE_COMMISSION_PLAN_MESSAGE:
                this.ProcessCommissionPlanMessage(message);
                break;
            case Message.CODE_TRADING_SYSTEM_MESSAGE:
                this.ProcessTradingSystemMessage(message);
                break;
            case Message.CODE_TRADING_SYSTEM_REMOVE_MESSAGE:
                this.ProcessTradingSystemRemoveMessage(message);
                break;
            case Message.CODE_SUBSCRIPTION_STRATEGY_MESSAGE:
                this.ProcessSubscriptionStrategyMessage(message);
                break;
            case Message.CODE_PFIX_EXTERNAL_LINK_MESSAGE:
                this.ExternalLinksCache.processExternalLinkMessage(message);
                break;
            case Message.CODE_PFIX_EXTERNAL_LINK_REMOVE_MESSAGE:
                this.ExternalLinksCache.processExternalLinkRemoveMessage(message);
                break;
            case Message.CODE_PFIX_CUSTODIAL_PLAN_MESSAGE:
                this.ProcessCustodialPlanMessage(message);
                break;
            case Message.CODE_RISK_WARN_MESSAGE:
                this.ProcessRiskRuleWarningMessge(message);
                break;
            case Message.CODE_ALERT_MESSAGE:
            case Message.CODE_REPLACE_ALERT_MESSAGE:
            case Message.CODE_CONTROL_ALERT_COMMAND_MESSAGE:
                this.AlertManager.NewMessage(message);
                break;
            case Message.CODE_NEWS_MESSAGE:
                this.NewsCache.processNewsMessage(message);
                break;
            case Message.CODE_USER_GROUP:
                this.handleUserGroupMSG(message);
                break;
            case Message.CODE_SUBSCRIBE_RESPONSE_MESSAGE:
                this.ProcesSubscribeResponseMessage(message);
                break;
            case Message.CODE_PFIX_PRODUCT_MESSAGE:
            case Message.CODE_PFIX_PRODUCT_DOCUMENT_RESP:
            case Message.CODE_PFIX_PRODUCT_SUBSCRIPTION_MANAGEMENT_RESP:
                this.EntitlementManager.NewMessage(message);
                break;
            case Message.CODE_SOURCE_LIST:
                this.ProcessSourceMappingMessage(message);
                break;
            case Message.CODE_PFIX_PORTFOLIO_STATISTICS_RESPONSE:
            case Message.CODE_PFIX_PORTFOLIO_MODEL_MESSAGE:
            case Message.CODE_PFIX_PORTFOLIO_SYSTEM_MESSAGE:
            case Message.CODE_PFIX_PORTFOLIO_INVESTOR_MESSAGE:
            case Message.CODE_PFIX_PORTFOLIO_REALTIME_STATISTICS:
                if (this.PortfolioCache) {
                    this.PortfolioCache.NewMessage(message);
                }
                break;
            case Message.CODE_PFIX_ALGORITHM_SUBSCRIBE_MESSAGE:
            case Message.CODE_PFIX_ALGORITHMS_AVAILABLE_MESSAGE:
            case Message.CODE_PFIX_ALGORITHM_GAINERS_LOSERS_MESSAGE:
            case Message.CODE_PFIX_ALGORITHM_HIGH_LOW_MESSAGE:
            case Message.CODE_PFIX_ALGORITHM_TOP_VOLUME_MESSAGE:
            case Message.CODE_PFIX_ALGORITHM_HISTORICAL_GAINER_LOSER_TOPS_MESSAGE:
            case Message.CODE_PFIX_ALGORITHM_HEATMAP_MESSAGE:
                if (this.AlgorithmCache) {
                    this.AlgorithmCache.NewMessage(message);
                }
                break;
            case Message.BM_COUNTER_MESSAGE:
                this.ProcessBMCounterMessage(message);
                break;
            case Message.CODE_PRICE_LIMITS_MESSAGE:
                this.ProcessPriceLimitsMessage(message);
                break;
            case Message.CUSTOM_LIST_MESSAGE:
                this.processCustomLists(message);
                break;
            case Message.CODE_PFIX_FUNDING_RATE_MARKUP_PLAN_MESSAGE:
                this.ProcessFundingRateMarkupPlanMessage(message);
                break;
            case Message.CODE_PFIX_CLOSE_ACCOUNT_RESPONSE_MESSAGE:
                this.ProcessCloseAccountResponseMessage(message);
                break;
            case Message.CODE_PFIX_MAM_GROUP_MESSAGE:
                this.UpdateMamGroups(message);
                break;
            case Message.CODE_HISTORY_CHANGED_RANGE:
                this.handleHistoryChanged(message);
                break;
            case Message.CODE_PFIX_ACCOUNT_FOLLOWER_RESP:
                this.handleAccountFollowerResponse(message);
                break;
            case Message.CODE_INTERESTS_ON_BALANCE_PLAN_MESSAGE:
                this.updateInterestRate(message);
                break;
        }
    }
    handleAccountFollowerResponse(message) {
        this.OnAccountFollowerResponse.Raise(message);
    }
    UpdateMamGroups(message) {
        MamGroupsCache.addGroup(message);
    }
    handleHistoryChanged(message) {
        this.OnHistoryChangedAndNeedReload.Raise(message);
    }
    processCustomLists(message) {
        this.customInstrumentListsCache.addOrUpdate(message);
    }
    GetCustomListsByType(type) {
        return this.customInstrumentListsCache.getCustomListsByType(type);
    }
    addRiskPlan(msg) {
        const id = msg.Id;
        const riskPlanDict = this.riskPlanDict;
        let riskPlan = null;
        if (!riskPlanDict.hasOwnProperty(id)) {
            riskPlan = riskPlanDict[id] = new RiskPlan();
        }
        else {
            riskPlan = riskPlanDict[id];
        }
        if (riskPlan) {
            riskPlan.update(this, msg);
        }
        else {
            console.log('no riskPlan');
        }
        this.lastRiskPlanItem = riskPlan;
        for (let i = 0; i < msg.Data.length; i++) {
            this.productTypeEnabilityCache[msg.Data[i].Item.ProductType] = true;
        }
    }
    AddInstrumentType(msg) {
        const type = new InstrumentGroup(msg, this);
        this.instrTypes[type.TypeId] = type;
    }
    getInstrumentTypeById(typeId) {
        const type = this.instrTypes[typeId];
        return type || null;
    }
    FindInstrumentTypes(typeId, list) {
        list.push(typeId);
        try {
            for (const type in this.instrTypes) {
                const instrumentType = this.instrTypes[type];
                if (instrumentType.SuperTypeId == typeId) {
                    this.FindInstrumentTypes(instrumentType.Id, list);
                }
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
        }
    }
    processTradeSessionStatusMessage(msg) {
        const tradeSessionStatusCache = this.tradeSessionStatusCache;
        let sessionStatus = tradeSessionStatusCache[msg.Id];
        if (sessionStatus) {
            sessionStatus.update(msg);
            this.OnTradingSessionStatusUpdated.Raise(sessionStatus);
        }
        else {
            sessionStatus = new TradeSessionStatus(msg);
            tradeSessionStatusCache[sessionStatus.Id] = sessionStatus;
        }
    }
    handleYTMMessage(message) {
        if (isNullOrUndefined(message.InstrumentID)) {
            return;
        }
        const instruments = this.GetInstrumentsByInstrumentId(message.InstrumentID.toString());
        if (!isValidArray(instruments)) {
            return;
        }
        instruments.forEach((ins) => {
            ins.YieldRate = message.YieldRate;
        });
    }
    handleQuoteMessage(msg) {
        this.FQuoteCache.newQuoteMessage(msg);
    }
    // TODO. Rename.
    HandleReportMessageDirect(msg) {
        if (Resources.isHidden('reports.' + msg.Name)) {
            return;
        }
        const businessRejectCode = msg.BusinessRejectCode;
        if (isValidNumber(businessRejectCode) &&
            (businessRejectCode === BusinessRejectMessage.TICKER_FOR_SUBSCRIPTION_NOT_EXIST ||
                businessRejectCode === BusinessRejectMessage.REQUEST_CODE_IS_NOT_SUPPORTED)) {
            return;
        }
        const event = EventCache.FromReportMessage(msg);
        if (EventCache.isDailyLimitExist(event)) {
            return;
        }
        this.OnReportMessage.Raise(msg);
        EventCache.Add(event);
    }
    HandleException(ex) {
        EventCache.AddException(ex);
    }
    HandleDirectAssetMessage(assetMessage) {
        let asset = null;
        if (this.assetCollection.hasOwnProperty(assetMessage.Id)) {
            asset = this.assetCollection[assetMessage.Id];
            asset.FillByMessage(assetMessage);
        }
        else {
            asset = new Asset(this, assetMessage);
            this.assetCollection[assetMessage.Id] = asset;
        }
        this.OnUpdateAssetEvent.Raise(this, asset);
    }
    HandleCrossRateMessageDirect(msg) {
        this.CrossRateCache.Update(msg);
    }
    HandleSessionMessageDirect(msg) {
        const session = this.SessionCache.newMessage(msg);
        if (this.Loaded) {
            const arr = this.Instruments;
            for (const key in arr) {
                // Apply session to instruments
                const instr = arr[key];
                if (instr.TradingSessionsId == session.Id) {
                    instr.ApplySession(session);
                    this.OnUpdateInstrument.Raise(instr);
                }
            }
            BaseSettings.onSettingsChanged();
        }
    }
    handleReportTypeMessage(msg) {
        if (msg.accessType & AccessType.User) {
            this.createAllowedReport(AllowedReport.REPORT_KEY_PREFIX + msg.name, msg.id, msg.parameters);
        }
        if (msg.accessType & AccessType.Technical) {
            this.createAllowedReport(AllowedReport.TECHNICAL_REPORT_KEY_PREFIX + msg.name, msg.id, msg.parameters);
        }
    }
    // TODO.
    HandlePropertyMessageDirect(propertyMessage) {
        var _a;
        if (propertyMessage.Name === FINISH_BLOCK_TRANSFER) {
            if (this.NeedRedirectToPortfolio()) {
                /* doc. раздел 3) -> третий пункт с белым кружочко:  https://docs.google.com/document/d/1Tnb1LqXehcolTHU2ZEOywAuHLcYOXWA3Grsjddu0C-E/edit#heading=h.9o7dj749n3zi */
                return;
            }
            if (Connection.vendor) {
                /* эта проверка необходима для работы Unit-тестов */
                this.FLogin = Connection.vendor.login;
                this.UserLogin = Connection.vendor.login;
            }
            this.LoginUser = this.getUserByLogin(this.FLogin);
            // TODO  MainAccountNew
            if ((_a = this.LoginUser) === null || _a === void 0 ? void 0 : _a.Accounts.length) {
                this.MainAccountNew = this.LoginUser.Accounts[0];
            }
            else {
                this.MainAccountNew = null;
            }
            this.Loaded = true;
            this.AlertManager.AlertsVisible =
                !Resources.isHidden('screen.Alerts.visibility') && this.isAllowedForMyUser(RulesSet.FUNCTION_ALERTS);
            this.AlertManager.ProcessDeferredMessages();
            this.updateOwnedAccounts();
            // кешируем инструменты в массив.
            if (!this.NonFixedList) {
                const keys = Object.keys(this.Instruments);
                this.InstrumentsArray = keys.map(function (value) {
                    return this.Instruments[value];
                }.bind(this));
            }
            void this.ExternalLinksCache.ytStartScheduler();
            EventCache.loaded();
            this.LinkPlansAndAccounts();
            this.OnReintialize.Raise();
            this.ReSubscribe();
            this.ProcessDeferredMessages(null);
            if (this.LoadedResolve) {
                this.LoadedResolve();
            }
            this.LoadedResolve = null;
            this.cacheUserHasSameCurrencyOnAllAccounts();
            this.userBaseCurrency = this.getUserBaseCurrency(); // TODO нужно вызывать после кадждого открытия/закрытия позиций для выполнения пункта 4 из правил получения валюты аккаунта https://ibb.co/HVtD0gw
        }
        if (propertyMessage.Name === RulesSet.FUNCTION_NON_FIXED_LIST) {
            this.NonFixedList = propertyMessage.Value === 'true';
        }
        else if (propertyMessage.Name === RulesSet.AVAILABLE_NUMBER_OF_SNAPSHOT_REQUESTS ||
            propertyMessage.Name === RulesSet.VALIDATE_MAX_LOT_CLOSE_ORDER) {
            // вероятно выглядет немного костыльно, но спасибо серверу решили выделить
            // AVAILABLE_NUMBER_OF_SNAPSHOT_REQUESTS от остальных
            // и это не ложится на нашу схему с рулами
            // потому ручками чуть правим Name и Value под общую схему
            this.RulesCache.AddRule(propertyMessage.OwnerType, propertyMessage.OwnerID, propertyMessage.Name, propertyMessage.Value);
        }
        else if (propertyMessage.OwnerID == DirectPropertyOwner.FeautreFlags) {
            this.RulesCache.AddFeatureFlag(propertyMessage.Name, propertyMessage.Value);
        }
        else if (propertyMessage.Name === 'RULE') {
            // раздельный ресурсоемкий рул
            const ownerID = propertyMessage.OwnerID;
            const ownerType = propertyMessage.OwnerType;
            let ruleKey = propertyMessage.Value;
            let ruleValue = '1';
            const parts = ruleKey.split('=');
            if (parts.length === 2) {
                ruleKey = parts[0];
                ruleValue = parts[1];
            }
            if (ruleKey === RulesSet.VALUE_QUOTE_HISTORY_DEPTH_LIMITS) {
                const parsedObject = JSON.parse(ruleValue);
                const depthLimitsMap = new Map(Object.entries(parsedObject).map(([key, value]) => [
                    key,
                    new HistoryDepthLimit(value.ticks, value.minBars, value.dayBars)
                ]));
                ruleValue = depthLimitsMap;
            }
            if (ruleKey === RulesSet.FUNCTION_BASE_CURRENCY) {
                this.baseCurrency = ruleValue;
            }
            else if (ruleKey === RulesSet.VALUE_RISK_ID && this.MainAccountNew) {
                if (this.MainAccountNew.AcctNumber === ownerID.toString()) {
                    this.lastRiskPlanItem = this.TryGetRiskPlan(ruleValue);
                    this.lastRiskPlanItem.applyToInstrument(this);
                }
            }
            else {
                this.RulesCache.AddRule(ownerType, ownerID, ruleKey, ruleValue);
            }
            // TODO.
            // needCallIsAllowedChanged = true
        }
        else if (propertyMessage.Name === RulesSet.FUNCTION_USE_ONE_TIME_PASSWORD) {
            TradingLockUtils.TradingLock.LockTradingByPassword = true;
            TradingLockUtils.TradingLock.updateTradingLock(true);
        }
    }
    createAllowedReport(name, id, parameters) {
        const report = new AllowedReport(name, id, parameters);
        this.reportDict[name] = report;
    }
    UpdateAccountsCommissionPlan() {
        for (const acc in this.Accounts) {
            const account = this.Accounts[acc];
            account.CommissionPlan = this.TryGetCommissionPlan(account.CommisionId);
        }
    }
    LinkPlansAndAccounts() {
        const mainAccount = this.LoginUser != null ? this.LoginUser.Accounts[0] : null;
        for (const acc in this.Accounts) {
            const account = this.Accounts[acc];
            account.SpreadPlan = this.TryGetSpreadPlan(account.SpreadPlanId);
            if (account.CommissionPlan === null) {
                account.CommissionPlan = this.TryGetCommissionPlan(account.CommisionId);
            }
            if (account.CommissionPlan2 === null) {
                account.CommissionPlan2 = this.TryGetCommissionPlan(account.CommisionId2);
            }
            if (account.CommissionPlan3 === null) {
                account.CommissionPlan3 = this.TryGetCommissionPlan(account.CommisionId3);
            }
            if (account.CrossratesPlan === null) {
                account.CrossratesPlan = this.TryGetCrossratesPlan(account.CrossratesPlanId);
            }
            if (account.RiskPlan === null) {
                account.RiskPlan = this.TryGetRiskPlan(account.RiskPlanId);
            }
            if (account.SwapPlan === null) {
                account.SwapPlan = this.TryGetSwapPlan(account.SwapPlanId);
            }
            if (account.ChargingPlan === null) {
                account.ChargingPlan = this.TryGetChargingPlan(account.ChargingPlanId);
            }
        }
        if (mainAccount === null || mainAccount === void 0 ? void 0 : mainAccount.RiskPlan) {
            this.ApplyRisksForInstrument(mainAccount.RiskPlan);
        }
    }
    ApplyRisksForInstrument(rplan) {
        try {
            const mass = this.getCachedInstruments();
            for (let i = 0; i < mass.length; i++) {
                const instrument = mass[i];
                instrument.RiskSettings.RiskPlanSettings = rplan.GetRisksForInstrument(instrument.GetInteriorID());
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
        }
    }
    ProcessLogin(login) {
        return __awaiter(this, void 0, void 0, function* () {
            yield new Promise(function (resolve, reject) {
                this.LoadedResolve = resolve;
            }.bind(this));
            return yield this.ThirdPartyProvidersValidator.validateLoginAsync(Connection.vendor, login);
        });
    }
    getFirstTradableInstrument() {
        const instruments = this.Instruments;
        let firstVisibleInstrument = null;
        for (const insId in instruments) {
            const ins = instruments[insId];
            if (isNullOrUndefined(firstVisibleInstrument)) {
                firstVisibleInstrument = ins;
            }
            if (ins.RouteIsTradable()) {
                return ins;
            }
        }
        return firstVisibleInstrument;
    }
    HandleAccountStatusMessageDirect(accountStatusMessage) {
        const accKey = accountStatusMessage.BstrAccount;
        const accFromStorage = this.Accounts[accKey];
        if (accountStatusMessage.Status === 2) {
            if (accFromStorage) {
                delete this.Accounts[accKey];
                this.updateOwnedAccounts();
                this.OnRemoveAccount.Raise(accFromStorage);
            }
        }
        else {
            if (accFromStorage) {
                accFromStorage.FillAccount(accountStatusMessage, true);
                this.OnUpdateAccount.Raise(accFromStorage);
            }
            else {
                const newAcc = new Account(this);
                newAcc.FillAccount(accountStatusMessage, false);
                this.Accounts[accKey] = newAcc;
                this.updateOwnedAccounts();
                this.OnAddAccount.Raise(newAcc);
            }
        }
        this.getNumberOfAccounts(true);
    }
    handleUserGroupMSG(mess) {
        this._userGroups[mess.GroupId] = mess;
    }
    GetUserGroupById(id) {
        return this._userGroups[id] || null;
    }
    CreateOrUpdateUser(mess, acc) {
        let user = acc.User;
        if (user === null) {
            user = this.getUserByUserId(mess.UserID);
            // Не было юзера
            if (user === null) {
                // fixed by nicky
                // старый сервер, такой как интеграл.нет не присылает UserLogin и UserID
                // валится огромное число обработанных и нет нуллреференсов
                const login = mess.UserLogin;
                const userPin = mess.UserID !== null ? mess.UserID : mess.Pin;
                user = new User(userPin, mess.Role, mess.Status, login, mess.ClientType, mess.Email, mess.GroupId);
                this.Users.set(mess.UserID, user);
            }
            acc.User = user;
            user.Accounts.push(acc);
        }
        else {
            // user.LockedByLogin = lockedBy;
            if (mess.Status !== null) {
                user.Status = mess.Status;
            }
            if (mess.ClientType !== null) {
                user.ClientType = mess.ClientType;
            }
            if (mess.GroupId !== null) {
                user.GroupId = mess.GroupId;
            }
        }
    }
    getUserByUserId(id) {
        return this.Users.get(id) || null;
    }
    getUserByLogin(login) {
        for (const user of this.Users.values()) {
            if (user.Login === login) {
                return user;
            }
        }
        return null;
    }
    // TODO. Refactor.
    HandleInstrumentMessageDirect(mess) {
        var _a;
        if (!(mess === null || mess === void 0 ? void 0 : mess.Name) || !mess.RoutesSupported) {
            console.log('Error creating instrument from empty instrument message.');
            return;
        }
        this.AddExchangeToCache(mess.TradingExchange);
        const hasExp = (_a = mess.InstrDateSettingsList) === null || _a === void 0 ? void 0 : _a.length;
        if (!hasExp &&
            (mess.InstrType === InstrumentTypes.OPTIONS ||
                mess.InstrType === InstrumentTypes.FUTURES ||
                mess.InstrType === InstrumentTypes.CFD_FUTURES)) {
            return;
        }
        this.cacheInstrument(mess);
        const validRouteIDs = this.Routes.getValidRouteIDs(mess.TradableRoutes, mess.RoutesSupported);
        for (const routeID of validRouteIDs) {
            const instruments = this.GenerateInstruments(mess, routeID);
            if (instruments) {
                for (let j = 0; j < instruments.length; j++) {
                    this.AddOrUpdateInstrumentFromMessage(instruments[j], mess);
                }
            }
        }
        if (mess.InstrType !== InstrumentTypes.OPTIONS && isValidArray(mess.underlierOptionInfo)) {
            this.GenerateFakeOptions(mess);
        }
    }
    /* TODO */
    cacheInstrument(message) { }
    AddOrUpdateInstrumentFromMessage(ins, mess) {
        var _a;
        if (this.Loaded &&
            !IsAllowed.Check_Instrument_For_Account_Visibility(this.getSortedAccountsArray(false, false), ins)) {
            return; /* #128189 */
        }
        this.tryUpdateExistingInstrumentTypes(ins);
        void this.CountryCache.fetchFlagIfNotAlready(ins.CountryId);
        const instruments = this.Instruments;
        const id = ins.InstrumentTradableID;
        if (id) {
            const interiorIdWtihoutRoute = InteriorIdUtils.generateInteriorIDWithoutRoute(ins.InstrumentTradableID, ins.InstrumentSpecificType);
            const Route_U = ins.Route;
            const i_contains = !!this.InstrumentsTableByIDs[Route_U];
            const isContinuousContract = ins.InstrumentSpecificType === InstrumentSpecificType.ContinuousContract;
            if (!i_contains) {
                this.InstrumentsTableByIDs[Route_U] = {};
            }
            const exist = this.InstrumentsTableByIDs[Route_U][interiorIdWtihoutRoute];
            if (!exist) {
                this.InstrumentsTableByIDs[Route_U][interiorIdWtihoutRoute] = ins;
            }
            if (ins.isFuturesSymbol() && isContinuousContract) {
                if (!this.InstrumentsContinuousContractIDS[ins.Id]) {
                    this.InstrumentsContinuousContractIDS[ins.Id] = {};
                }
                if (!this.InstrumentsContinuousContractIDS[ins.Id][Route_U]) {
                    this.InstrumentsContinuousContractIDS[ins.Id][Route_U] = {};
                }
                if (!this.InstrumentsContinuousContractIDS[ins.Id][Route_U].CurrentContinuousContract) {
                    this.InstrumentsContinuousContractIDS[ins.Id][Route_U].CurrentContinuousContract = ins;
                }
            }
        }
        const InsInteriorId = ins.GetInteriorID();
        const ContinuousContract = ins.InstrumentSpecificType === InstrumentSpecificType.ContinuousContract;
        const itidccc = this.InstrumentContinuousContractsByInteriorIDCache;
        const itidc = this.InstrumentsByInteriorIDCache;
        const hasNewCach = ContinuousContract
            ? itidccc.hasOwnProperty(InsInteriorId)
            : itidc.hasOwnProperty(InsInteriorId);
        if (instruments.hasOwnProperty(InsInteriorId) && hasNewCach) {
            const updateMessage = ((_a = mess === null || mess === void 0 ? void 0 : mess.InstrDateSettingsList) === null || _a === void 0 ? void 0 : _a.length) ? ins.CreateInstrumentMessage() : mess; // возникло в связи с комментом в #88334 youbanoe gouno над которым надо хорошенько подумать!!!
            ins = instruments[InsInteriorId];
            if (updateMessage) {
                if (BaseSettings.showInstrumentHalt &&
                    updateMessage.TradingMode === TradingMode.TradingHalt &&
                    ins.TradingMode !== TradingMode.TradingHalt) {
                    this.LogTradingHaltWarning(ins.DisplayName());
                }
                ins.UpdateByMessage(updateMessage);
                ins.UpdateHideRouteMode();
            }
            this.OnUpdateInstrument.Raise(ins);
        }
        else {
            if (ins.InstrumentSpecificType === InstrumentSpecificType.ContinuousContract) {
                this.InstrumentContinuousContractsByInteriorIDCache[InsInteriorId] = ins;
            }
            else {
                this.InstrumentsByInteriorIDCache[InsInteriorId] = ins;
            }
            instruments[InsInteriorId] = ins;
            const t_id = ins.TradingSessionsId;
            if (!this.InstrumentsByTradingSessionsId[t_id]) {
                this.InstrumentsByTradingSessionsId[t_id] = [];
            }
            this.InstrumentsByTradingSessionsId[t_id].push(ins);
            if (this.lastRiskPlanItem) // #89151
             {
                this.lastRiskPlanItem.processOneInstrument(ins);
            }
            this.AddAggregatedInstrument(ins);
            ins.UpdateHideRouteMode();
            let instrumentsByIdArray = this.InstrumentsByIdCache.get(ins.Id);
            if (isNullOrUndefined(instrumentsByIdArray)) {
                instrumentsByIdArray = [];
                this.InstrumentsByIdCache.set(ins.Id, instrumentsByIdArray);
            }
            instrumentsByIdArray.push(ins);
            if (!this.InstrumentsByTradableIdCache[id]) {
                this.InstrumentsByTradableIdCache[id] = [];
            }
            this.InstrumentsByTradableIdCache[id].push(ins);
            if (this.Loaded) {
                this.ProcessDeferredMessages(ins);
            }
            this.OnAddInstrument.Raise(ins);
        }
        const dsTrID = ins.GetDataSourceTradableId();
        if (dsTrID) {
            const dsIns = this.GetDataSourceInstrument(ins);
            if (!dsIns) {
                return;
            }
            const dsInteriorID = dsIns.GetInteriorID();
            if (!this.InstrumentsBySourceRouteIdTradableId[dsInteriorID]) {
                this.InstrumentsBySourceRouteIdTradableId[dsInteriorID] = [];
            }
            if (this.InstrumentsBySourceRouteIdTradableId[dsInteriorID].indexOf(ins) == -1) {
                this.InstrumentsBySourceRouteIdTradableId[dsInteriorID].push(ins);
            }
        }
    }
    tryUpdateExistingInstrumentTypes(instrument) {
        if (isNullOrUndefined(instrument)) {
            return;
        }
        const insType = instrument.InstrType;
        if (insType === InstrumentTypes.INDICIES) {
            return;
        }
        const instrumentType = instrument.CFD ? InstrumentTypes.EQUITIES_CFD : insType;
        if (!this.ExistingInstrumentTypes.Contains(instrumentType)) {
            this.ExistingInstrumentTypes.push(instrumentType);
        }
    }
    isExistForAccount(name, account) {
        return this.RulesCache.IsExistForAccount(name, account);
    }
    isAllowedForAccount(name, account) {
        return this.RulesCache.IsAllowedForAccount(name, account);
    }
    isAllowedForMainAccount(name) {
        return this.RulesCache.IsAllowedForAccount(name, null); // main account
    }
    isAllowedForMyUser(ruleKey) {
        return this.RulesCache.IsAllowedForMyUser(ruleKey);
    }
    isExistForMyUser(ruleKey) {
        return this.RulesCache.IsExistForMyUser(ruleKey);
    }
    isAllowedForUser(name, u) {
        return this.RulesCache.IsAllowedForUser(name, u);
    }
    isAllowedFeatureFlag(ruleName) {
        return this.RulesCache.IsAllowedFeatureFlag(ruleName);
    }
    boolRuleValue(ruleName, account) {
        const ruleValue = this.getRuleStringValueForAccount(ruleName, account);
        return ruleValue === '1' || ruleValue === 'true';
    }
    getRuleStringValueForAccount(ruleName, account, defaultValue) {
        const ruleValue = this.RulesCache.GetRuleValue(ruleName, account);
        if (!ruleValue) {
            return defaultValue;
        }
        return ruleValue;
    }
    getRuleNumberValueForAccount(ruleName, account, defaultValue) {
        return +this.getRuleStringValueForAccount(ruleName, account, defaultValue);
    }
    getRuleStringValueForMyUser(ruleName, defaultValue) {
        const ruleValue = this.RulesCache.GetRuleValueForMyUser(ruleName);
        if (!ruleValue) {
            return defaultValue;
        }
        return ruleValue;
    }
    getRuleNumberValueForMyUser(ruleName, defaultValue) {
        return +this.getRuleStringValueForMyUser(ruleName, defaultValue);
    }
    IsColumnAvailable(groupRule, columnRule) {
        var _a, _b;
        if (!isValidString(groupRule) || !isValidString(columnRule))
            return true;
        const s = (_a = this.getRuleStringValueForMyUser(groupRule, '')) !== null && _a !== void 0 ? _a : '';
        if (!isValidString(s))
            return true;
        try {
            const cfg = (_b = JSON.parse(s)) === null || _b === void 0 ? void 0 : _b[columnRule];
            return isNullOrUndefined(cfg) ? true : cfg;
        }
        catch (_c) {
            return true;
        }
    }
    /**
     * Retrieves the boolean value for the rule named VALIDATE_MAX_LOT_CLOSE_ORDER from the DataCache.
     * This rule is stored in RulesCache.#GlobalRules as either 'true' or 'false'.
     * returns {boolean} - The boolean value for the specified rule.
     */
    getValidateMaxLotCloseOrderBoolRule() {
        return this.getRuleStringValueForAccount(RulesSet.VALIDATE_MAX_LOT_CLOSE_ORDER) === 'true';
    }
    ActiveOrdersList(addSLTP) {
        return [];
    }
    AddNewTrade(tradeMSg) {
        let trade = this.newTradesDict[tradeMSg.TradeId];
        if (tradeMSg.Route == '' || !tradeMSg.InstrumentTradableID) {
            return;
        }
        const ins = this.getInstrumentByTradable_ID(tradeMSg.InstrumentTradableID, tradeMSg.Route);
        if (!ins) {
            this.AddDeferredMessage(tradeMSg);
            return;
        }
        // Обновляем информацию по объемам по паре аккаунт/инструмент для ассетов
        this.ProcessVolumeQuote(tradeMSg);
        if (!trade) {
            trade = new NewTrade(this, tradeMSg);
            this.newTradesDict[trade.TradeId] = trade;
            this.realTimeFilledOrdersArray.push(trade);
            this.filledOrdersArray.push(trade);
        }
        else {
            trade.UpdateByMessage(tradeMSg);
        }
        if (this.Loaded) {
            this.OnAddNewTradeEvent.Raise(this, trade);
        }
    }
    UpdateTrade(msg) {
        var _a;
        const trade = (_a = this.newTradesDict[msg.TradeId]) !== null && _a !== void 0 ? _a : new NewTrade(this, msg);
        trade.UpdateByMessage(msg);
        if (this.Loaded) {
            this.OnUpdateTradeEvent.Raise(trade);
        }
    }
    ProcessVolumeQuote(message) {
        // При расчете Today traded qty учитываем значение настройки Asset trading mode. Если Delivery, то в данном показателе учитываем
        // только трейды c Trading mode = Delivery, если General, то учитываем только трейды с Trading mode = General.
        if (!this.Loaded || message == null) {
            return;
        }
        const symbol = this.getInstrumentByTradable_ID(message.InstrumentTradableID, message.Route);
        if (symbol == null) {
            return;
        }
        const assetName = symbol.AssetName;
        if (!assetName) {
            return;
        }
        if (!this.IsSharedAsset(assetName)) {
            return;
        }
        const account = this.GetAccountByIdOrName(message.AccountId);
        if ((account === null || account === void 0 ? void 0 : account.AssetSharesNames) == null) {
            return;
        }
        if (!account.AssetSharesNames.includes(assetName)) {
            return;
        }
        const assetDataInfo = account.GetAssetDataInfoByName(assetName);
        const assetType = assetDataInfo.AssetTradingMode;
        const tradeType = message.ProductType;
        if ((assetType === AssetTradingMode.General && tradeType === ProductType.General) ||
            (assetType === AssetTradingMode.Delivery && tradeType === ProductType.Delivery) ||
            assetType === AssetTradingMode.All) {
            const amount = message.Amount * symbol.LotSize;
            assetDataInfo.TodayTradedQty += message.BuySell === OperationType.Buy ? amount : -amount;
        }
    }
    IsSharedAsset(name) {
        const asset = this.GetAssetByName(name);
        return asset != null && asset.Type == AssetType.SHARES;
    }
    AddExchangeToCache(exchange) {
        if (exchange && !this.TradingExchanges.includes(exchange)) {
            this.TradingExchanges.push(exchange);
        }
    }
    // TODO Optimization
    FindInstrumentByTradingExchange(exchange) {
        const keys = Object.keys(this.Instruments);
        const res = {};
        for (let i = 0; i < keys.length; i++) {
            const ins = this.Instruments[keys[i]];
            if (ins.TradingExchange === exchange) {
                res[ins.DisplayName()] = ins.GetInteriorID();
            }
        }
        const resKeys = Object.keys(res);
        resKeys.sort((a, b) => a.localeCompare(b));
        const resId = res[resKeys[0]];
        return this.Instruments[resId] || null;
    }
    // #endregion Direct
    GetFilteredTrades(instrument, account) {
        const orders = [];
        if (instrument != null && account != null) {
            const list = this.realTimeFilledOrdersArray;
            for (const trade of list) {
                if (trade.Account === account &&
                    Instrument.IsEqualInstrument(trade.Instrument, instrument) &&
                    trade.Instrument.Route === instrument.Route) {
                    orders.push(trade);
                }
            }
        }
        return orders;
    }
    GetTradeSessionStatus(id) {
        const sessionStatus = this.tradeSessionStatusCache[id];
        if (!isNullOrUndefined(sessionStatus)) {
            return sessionStatus;
        }
        return null;
    }
    /** Retrieves the primary account of the user. @documentation [User Primary Account](https://docs.google.com/document/d/1guol3zJqsotNQ8jTVB1VbVt1j4Pggl8E7DeAYFzHRYc/edit#bookmark=id.b188g5i9xrs7) @returns {Account | null} The user's primary account, or null if none is found. */
    getPrimaryAccount() {
        const accs = this.Accounts;
        for (const accID in accs) {
            const acc = accs[accID];
            if (acc.IsOwnedByUser) {
                return acc;
            }
        }
        return null;
    }
    getUserID() {
        const acc = this.getPrimaryAccount();
        return acc ? acc.userID : null;
    }
    getUserName() {
        return this.UserLogin;
    }
    getAllowedReportDict() {
        return this.reportDict;
    }
    getCachedInstruments() {
        // TODO бля потом переделывать 100%
        return Object.keys(this.Instruments).map(function (value) {
            return this.Instruments[value];
        }.bind(this));
        /// / Тут я Никиту не понял ???
        // if (this.NonFixedList)
        //    return getSortedInstruments();
        // if (this.cachedInstrumentsWR == null || this.cachedInstrumentsWR.length == 0)
        // {
        //    //на всякий
        //    //TODO IMPLEMENT
        //    //this.GenerateOptions();
        //    this.cachedInstrumentsWR = this.getSortedInstruments();
        // }
        // return this.cachedInstrumentsWR;
    }
    ProcessTradeSessionSwitchMessage(msg) {
        const instruments = this.GetInstrumentsBySessionID(msg.SessionId);
        if (instruments.length === 0) {
            console.log('TradeSessionSwitchMessage', "DataCache::ProcessTradeSessionSwitchMessage::Can't find instruments by session id:" + msg.SessionId, EventType.Exception);
            return;
        }
        for (let i = 0; i < instruments.length; i++) {
            instruments[i].TradeSessionStatusId = msg.TradeSessionStatusId;
            if (msg.ExchangeSessionName) {
                instruments[i].ExchangeSessionName = msg.ExchangeSessionName;
            }
        }
    }
    GetInstrumentsBySessionID(sessionId) {
        return this.InstrumentsByTradingSessionsId[sessionId] || [];
    }
    ProcessInstrumentUpdateMessage(message, continuouseContractSearch = false) {
        const ai = this.GetAggregatedInstrument(message.Name);
        if (ai === null) {
            if (!continuouseContractSearch) {
                console.log('InstrumentUpdateMessage', "DataCache::ProcessInstrumentUpdateMessage::Can't find AggregatedInstrument for symbol: " +
                    message.Name +
                    ' instrument id: ' +
                    message.InstrumentID +
                    (message.ContractID !== null ? ' contract id: ' + message.ContractID : ''), EventType.Exception);
            }
            return;
        }
        let isFutures = false;
        for (let i = 0; i < ai.length; i++) {
            const inst = ai[i];
            if (message.TradeSessionStatusId !== null && message.TradeSessionStatusId !== undefined) {
                inst.TradeSessionStatusId = message.TradeSessionStatusId;
            }
            if (message.ExchangeSessionName) {
                inst.ExchangeSessionName = message.ExchangeSessionName;
            }
            if (inst.InstrType == InstrumentTypes.FUTURES) {
                isFutures = true;
            }
        }
        if (isFutures && !continuouseContractSearch) {
            message.Name = '[' + message.Name + ']';
            this.ProcessInstrumentUpdateMessage(message, true);
        }
    }
    AddAggregatedInstrument(ins) {
        if (!this.AggregatedInstruments[ins.ShortName]) {
            this.AggregatedInstruments[ins.ShortName] = [];
        }
        if (ins.SourceName) {
            if (!this.AggregatedInstruments[ins.SourceName]) {
                this.AggregatedInstruments[ins.SourceName] = [];
            }
            this.AggregatedInstruments[ins.SourceName].push(ins);
        }
        this.AggregatedInstruments[ins.ShortName].push(ins);
    }
    GetAggregatedInstrument(name) {
        return this.AggregatedInstruments[name] || null;
    }
    AddSpreadPlan(message) {
        //
        // Spread plan
        //
        try {
            this.SpreadPlans[message.Id] = new SpreadPlan(this, message);
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
        }
    }
    GetSpreadPlan(account) {
        let id = null;
        if (account) {
            id = account.SpreadPlanId;
        }
        else if (this.MainAccountNew) {
            id = this.MainAccountNew.SpreadPlanId;
        }
        return this.SpreadPlans[id];
    }
    // получение массива HistoryType по периоду(для тиков берутся Trades и Ask или Bid)
    GetAllowedHistoryTypesByPeriod(period) {
        const listHistoryType = [];
        const allowedHistoryTypes = this.AllowedHistoryTypes;
        let isTickQuote = false;
        const len = allowedHistoryTypes.length;
        for (let i = 0; i < len; i++) {
            const allowedHistoryType = allowedHistoryTypes[i];
            // +++denis http://tp.pfsoft.net/Project/QA/Bug/View.aspx?BugID=16422&acid=74FA48A38CBEE17F6445F76C9BEC721B&list=STATE_0_ctl00_mainArea_projectCatalogZone1_ctl00_tdL_gA&pageIndex=1
            if (period === Periods.TIC) {
                if (!isTickQuote &&
                    (allowedHistoryType === HistoryType.BID || allowedHistoryType === HistoryType.ASK)) {
                    listHistoryType.push(allowedHistoryType);
                    isTickQuote = true;
                }
                else if (allowedHistoryType === HistoryType.LAST) {
                    listHistoryType.push(allowedHistoryType);
                }
            }
            else
                listHistoryType.push(allowedHistoryType);
        }
        return listHistoryType;
    }
    get AllowedHistoryTypes() {
        return this.FAllowedHistoryTypes ? this.FAllowedHistoryTypes.slice() : [];
    }
    // IInstrument instr, double volume, bool showLots, ProductType productType = ProductType.General
    formatVolume(instr, volume, showLots, productType, account) {
        const precision = instr === null || instr === void 0 ? void 0 : instr.getAmountPrecision(showLots, account, productType);
        return PriceFormatter.getNumberFormatter(precision).format(volume);
    }
    GetInstrumentList(patern, exchangeIDs, instrumentTypes, generateStrikes, aliasLanguage, oldLookupType) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.NonFixedList) {
                return yield Promise.resolve(this.InstrumentsArray);
            }
            return yield Connection.vendor
                .GetInstrumentList(InstrumentUtils.ParseSearchPattern(patern), exchangeIDs, instrumentTypes, aliasLanguage, oldLookupType)
                .then(this.ResponseGenerationFakeInstruments.bind(this, generateStrikes, false));
        });
    }
    GetOptionsList(patern, exchangeIDs, aliasLanguage) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.NonFixedList) {
                return yield Promise.resolve(Object.values(this.OptionsCache.getOptionInstruments(OptionsFilter.All)));
            }
            return yield Connection.vendor
                .GetOptionsList(InstrumentUtils.ParseSearchPattern(patern), exchangeIDs, [InstrumentTypes.OPTIONS, InstrumentTypes.CFD_FUTURES], aliasLanguage)
                .then(this.ResponseGenerationFakeInstruments.bind(this, false, true));
        });
    }
    ResponseGenerationFakeInstruments(generateStrikes, isOptionsMode, msg) {
        let result = [];
        const nonFixedInstrumentInfoArray = msg[0].NonFixedInstrumentInfoList;
        for (let i = 0; i < nonFixedInstrumentInfoArray.length; i++) {
            const entry = nonFixedInstrumentInfoArray[i];
            const im = this.createInstrumentMessageFromInstrumentInfo(entry);
            if (isValidNumber(im.CountryId)) {
                void this.CountryCache.fetchFlagIfNotAlready(im.CountryId);
            }
            const validRouteIDs = this.Routes.getValidRouteIDs(im.TradableRoutes, im.RoutesSupported);
            for (const routeID of validRouteIDs) {
                const route = this.Routes.getRouteById(routeID);
                const fakeInstruments = im.InstrType === InstrumentTypes.OPTIONS
                    ? this.generateFakeNonFixedOptions(entry, im, routeID, generateStrikes, isOptionsMode)
                    : this.GenerateInstruments(im, routeID);
                result = result.concat(fakeInstruments);
            }
        }
        return result;
    }
    createInstrumentMessageFromInstrumentInfo(info) {
        const im = new DirectInstrumentMessage();
        im.Name = info.Name;
        im.ExchangeId = info.ExchangeId;
        im.TradingExchange = info.TradingExchange;
        im.MarketDataExchange = info.MarketDataExchange;
        im.InstrumentTradableID = info.InstrumentTradableID;
        im.InstrType = info.InstrType;
        im.TypeId = info.GroupId;
        im.Id = info.ID;
        im.ForwardBaseInstrument = info.Underlier;
        im.ForwardBaseInstrumentUnderlier = info.UnderlierUnderlier;
        im.ForwardBaseInstrumentDescr = info.UnderlierDescr;
        im.ForwardBaseInstrumentCountryId = info.UnderlierCountryId;
        im.ForwardBaseInstrumentTradingExchange = info.UnderlierTradingExchange;
        // НУЖЕН норм. признак для формы лукапа
        im.Exp2 = Instrument.FAKE_NONFIXED_RESPONSE;
        im.InstrDateSettingsList = info.InstrDateSettingsList;
        im.UseOptionDescription = info.UseOptionDescription;
        im.AllExpDateLoaded = info.AllExpDateLoaded;
        im.AllContractsLoaded = info.AllContractsLoaded;
        im.SeriesGroupName = info.SerieName;
        im.Descr = info.Description;
        im.UnderlierDescription = info.Underlier;
        im.CountryId = info.CountryId;
        if (info.LanguageAliases != null) {
            im.LanguageAliases = Instrument.CloneLangAliases(info.LanguageAliases);
        }
        this.AddExchangeToCache(info.TradingExchange);
        for (let j = 0; j < info.Routes.length; j++) {
            const routeId = info.Routes[j];
            const route = this.Routes.getRouteById(routeId);
            if (route === null || route === void 0 ? void 0 : route.IsTradable) {
                im.TradableRoutes.push(routeId);
            }
            if (route !== null && route.IsInfo) {
                im.RoutesSupported.push(routeId);
            }
        }
        return im;
    }
    generateFakeNonFixedOptions(info, im, routeID, generateStrikes, isOptionsMode) {
        const result = [];
        if (isOptionsMode) {
            for (let i = 0; i < im.InstrDateSettingsList.length; i++) {
                const ds = im.InstrDateSettingsList[i];
                const isFutures = isValidNumber(ds.ContractID) && ds.ContractID > 0;
                const underlierId = info.UnderlierID.toString();
                const underlierTradableId = isFutures && isValidNumber(ds.UnderlierTradableId) ? ds.UnderlierTradableId.toString() : '';
                const underlierName = ds.Underlier;
                const optionMessage = structuredClone(im);
                optionMessage.Name = underlierName;
                optionMessage.Descr = info.UnderlierDescr;
                optionMessage.CountryId = info.UnderlierCountryId;
                optionMessage.InstrDateSettingsList = [ds];
                const instrument = new Instrument(this, optionMessage, routeID);
                let underlier = this.FindUnderlier(underlierId, routeID, underlierTradableId);
                if (isNullOrUndefined(underlier)) {
                    underlier = this.GenerateFakeUnderlier(instrument, underlierId, ds.UnderlierTradableId, underlierName, isFutures);
                }
                instrument.ForwardBaseInstrument = underlier;
                result.push(instrument);
            }
        }
        else {
            for (const instrDateSettings of im.InstrDateSettingsList) {
                this.GenerateFakeStrikes(result, im, instrDateSettings, routeID, info.PointSize, generateStrikes);
            }
        }
        return result;
    }
    getInstrumentById(instrumentID) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.NonFixedList) {
                return false;
            }
            let promise = this._instrumentsByIdRequests.get(instrumentID);
            if (!isNullOrUndefined(promise)) {
                return yield promise;
            }
            promise = this.createGetInstrumentByIdPromise(instrumentID);
            this._instrumentsByIdRequests.set(instrumentID, promise);
            const result = yield promise;
            this._instrumentsByIdRequests.delete(instrumentID);
            return result;
        });
    }
    getOptionById(optionID) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.NonFixedList) {
                return false;
            }
            let promise = this._optionsByIdRequests.get(optionID);
            if (!isNullOrUndefined(promise)) {
                return yield promise;
            }
            promise = this.createGetOptionByIdPromise(optionID);
            this._optionsByIdRequests.set(optionID, promise);
            const result = yield promise;
            this._optionsByIdRequests.delete(optionID);
            return result;
        });
    }
    _gIBId(msg) {
        if (!msg.length) {
            return false;
        }
        const _msg = msg[0];
        if (_msg.Code !== Message.CODE_INSTRUMENT) {
            return false;
        }
        this.NewMessage(_msg);
        return true;
    }
    getInstrumentByInstrumentTradableID_NFL(tradableId_1, routeId_1) {
        return __awaiter(this, arguments, void 0, function* (tradableId, routeId, interiorId = null, routeValidationIgnore = false) {
            if (!isValidNumber(tradableId)) {
                return null;
            }
            const route = this.Routes.getRouteById(routeId);
            if (isNullOrUndefined(route) && !routeValidationIgnore) {
                return null;
            }
            let instrument = this.getInstrumentByTradable_ID(tradableId, routeId);
            if (!isNullOrUndefined(instrument)) {
                return instrument;
            }
            instrument = this.getInstrumentByTradable_ID_first(tradableId);
            if (!isNullOrUndefined(instrument)) {
                return routeValidationIgnore ? instrument : null;
            }
            let promise = this._instrumentsByTradabldIdRequests.get(tradableId);
            if (!isNullOrUndefined(promise)) {
                return yield promise;
            }
            promise = this.createGetInstrumentByTradableIdPromise(tradableId, routeId, interiorId, routeValidationIgnore);
            this._instrumentsByTradabldIdRequests.set(tradableId, promise);
            const result = yield promise;
            this._instrumentsByTradabldIdRequests.delete(tradableId);
            return result;
        });
    }
    GetNonFixedInstrumentListByAssetName(AsssetName) {
        return Connection.vendor
            .GetNonFixedInstrumentListByAssetName(AsssetName)
            .then(this.ResponseGenerationFakeInstruments.bind(this, false, false));
    }
    GetNonFixedInstrumentStrikes(instrument) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield Connection.vendor
                .GetNonFixedInstrumentStrikes(instrument.ContractID)
                .then((msg) => {
                const im = instrument.CreateInstrumentMessage();
                const result = [];
                const findedStrikes = msg[0];
                for (let i = 0, len = im.InstrDateSettingsList.length; i < len; i++) {
                    const ds = im.InstrDateSettingsList[i];
                    if (findedStrikes != null) {
                        if (+ds.ContractMonthDate === +instrument.ExpDate) {
                            OptionContract.MergeOptionContractGroup(ds, findedStrikes.optionContractGroup);
                            this.GenerateFakeStrikes(result, im, ds, instrument.Route, findedStrikes.PointSize, true);
                        }
                    }
                }
                return result;
            });
        });
    }
    GenerateInstruments(mess, route) {
        var _a;
        const instruments = [];
        const isFuture = mess.InstrType === InstrumentTypes.FUTURES || mess.InstrType === InstrumentTypes.CFD_FUTURES;
        const isOption = mess.InstrType === InstrumentTypes.OPTIONS;
        if (isFuture) {
            // фьючерс детектед
            // New schema
            const list = mess.InstrDateSettingsList;
            for (let i = 0; i < list.length; i++) {
                const ds = list[i];
                if (ds.ContractMonthDate.getFullYear() <= 1970) {
                    continue;
                }
                const futureInstrument = this.GenerateFutureInstrument(mess, ds, route, false);
                instruments.push(futureInstrument);
                if (ds.IsContinuous) {
                    futureInstrument.haveContiniousContract = true;
                    if (!this.ContractsCache[route]) {
                        this.ContractsCache[route] = {};
                    }
                    if (mess.Id) {
                        this.ContractsCache[route][mess.Id] = futureInstrument;
                    }
                    instruments.push(this.GenerateFutureInstrument(mess, ds, route, true));
                }
            }
        }
        else if (isOption) {
            const forwardBaseInstrumentID = !isNullOrUndefined(mess.ForwardBaseInstrumentID)
                ? mess.ForwardBaseInstrumentID.toString()
                : '';
            const underlier = this.FindUnderlier(forwardBaseInstrumentID, route, '');
            if (isNullOrUndefined(underlier)) {
                // Option message waiting for underlier
                let delayedOptionsByUnderlierId = this._delayedOptionsByUnderlierIdRouteId.get(forwardBaseInstrumentID);
                if (isNullOrUndefined(delayedOptionsByUnderlierId)) {
                    delayedOptionsByUnderlierId = new Map();
                    this._delayedOptionsByUnderlierIdRouteId.set(forwardBaseInstrumentID, delayedOptionsByUnderlierId);
                }
                const delayedOptions = delayedOptionsByUnderlierId.get(route);
                if (isNullOrUndefined(delayedOptions)) {
                    delayedOptionsByUnderlierId.set(route, mess);
                }
                return [];
            }
            else {
                const options = this.GenerateStrikes(mess, route, underlier);
                for (let i = 0; i < options.length; i++) {
                    instruments.push(options[i]);
                }
            }
        }
        else {
            const instr = new Instrument(this, mess, route);
            instruments.push(instr);
        }
        if (!isOption) {
            const delayedOption = (_a = this._delayedOptionsByUnderlierIdRouteId.get(mess.Id)) === null || _a === void 0 ? void 0 : _a.get(route);
            if (!isNullOrUndefined(delayedOption)) {
                // Generate options with underlier
                const options = this.GenerateStrikes(mess, route, instruments[0]);
                for (let i = 0; i < options.length; i++) {
                    instruments.push(options[i]);
                }
                // Remove from delayed options
                const delayedOptionsByUnderlierId = this._delayedOptionsByUnderlierIdRouteId.get(mess.Id);
                delayedOptionsByUnderlierId.delete(route);
                if (delayedOptionsByUnderlierId.size === 0) {
                    this._delayedOptionsByUnderlierIdRouteId.delete(mess.Id);
                }
            }
        }
        return instruments;
    }
    GenerateStrikes(mess, route, underlier) {
        const instruments = [];
        for (let i = 0; i < mess.InstrDateSettingsList.length; i++) {
            const ds = mess.InstrDateSettingsList[i];
            const underlierTradableId = !isNullOrUndefined(ds.UnderlierTradableId)
                ? ds.UnderlierTradableId.toString()
                : '';
            const baseInstrument = isValidString(underlierTradableId)
                ? this.FindUnderlier(underlier.Id, route, underlierTradableId)
                : underlier;
            for (let j = 0; j < ds.StrikePricesList.length; j++) {
                const sett = ds.StrikePricesList[j];
                if (ds.ContractMonthDate.getFullYear() <= 1970) {
                    continue;
                }
                if (sett.CallEnabled) {
                    const optionInstr = this.GenerateOptionInstrument(mess, ds, route, OptionPutCall.OPTION_CALL_VANILLA, baseInstrument, sett);
                    if (optionInstr != null) {
                        instruments.push(optionInstr);
                    }
                }
                if (sett.PutEnabled) {
                    const optionInstr = this.GenerateOptionInstrument(mess, ds, route, OptionPutCall.OPTION_PUT_VANILLA, baseInstrument, sett);
                    if (optionInstr != null) {
                        instruments.push(optionInstr);
                    }
                }
            }
        }
        this.OptionsCache.addOption(mess, route, underlier);
        return instruments;
    }
    GenerateFutureInstrument(mess, ds, route, isContinuous) {
        let name = mess.Name;
        if (ds.FutureAliasName) {
            name = ds.FutureAliasName;
        }
        const instrument = this.GenerateInstrument(isContinuous ? '[' + name + ']' : name, mess, ds, route);
        if (isContinuous) {
            instrument.InstrumentSpecificType = InstrumentSpecificType.ContinuousContract;
            instrument.ContinuousContractName = name;
        }
        return instrument;
    }
    GenerateFakeOptions(mess) {
        if (!this.NonFixedList) {
            return;
        }
        const underlier = this.GetInstrumentsByInstrumentId(mess.Id)[0];
        // TODO look on
        // server: test13/qa2/123
        if (isNullOrUndefined(underlier)) {
            return;
        }
        for (let i = 0; i < mess.underlierOptionInfo.length; i++) {
            this.OptionsCache.addFakeOption(mess, mess.underlierOptionInfo[i], underlier);
        }
    }
    GenerateInstrument(name, mess, ds, route, forseCreateFake) {
        if (!forseCreateFake) {
            forseCreateFake = false;
        }
        const copy = mess.Clone();
        copy.Name = name;
        // TODO UNIC names
        // let useInstrumentFromCache = !!instrNames[fullName] && !forseCreateFake;
        const useInstrumentFromCache = false;
        // let der_instr = useInstrumentFromCache ? instrNames[fullName] : new Instrument(this, copy, route);
        const der_instr = new Instrument(this, copy, route);
        // +++ nfrolov: Если инструмента в кэше еще нет, то можем спокойно (и должны) установить все поля из мессэджа
        if ( /* context == GenerateInstrumentContext.Normal || */!useInstrumentFromCache) {
            der_instr.SourceName = mess.Name;
            der_instr.SeriesGroupName = mess.SeriesGroupName;
            der_instr.SourceDescription = mess.Descr;
            this.UpdateDerInstr(der_instr, ds, mess);
            if (mess.MarginCoeficientList.length > 0) {
                let futCoef = mess.MarginCoeficientList[0];
                for (const fc of mess.MarginCoeficientList) {
                    if (fc.FuturesExpYear == ds.ContractMonthDate.Year &&
                        fc.FuturesExpMonth == ds.ContractMonthDate.Month &&
                        fc.FuturesExpDay == ds.ContractMonthDate.Day) {
                        futCoef = fc;
                        break;
                    }
                }
                der_instr.RiskSettings.MarginCoefficients = futCoef;
            }
        }
        return der_instr;
    }
    GetBrandingRules(brandingKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const msgArr = yield Connection.vendor.GetBrandingRules(brandingKey);
            if (!isValidArray(msgArr)) {
                return;
            }
            const msg = msgArr[0];
            if (msg.Code !== Message.CODE_PFIX_BRANDING_RULES_RESP) {
                return;
            }
            if (msg.CompanyName) {
                this.CompanyName = msg.CompanyName;
            } // #85820
            if (msg.CompanyURL) {
                this.CompanyURL = msg.CompanyURL;
            } // #124451
            if (msg.BrokerInformation) {
                this.BrokerInformation = msg.BrokerInformation;
            } // #87238
            if (msg.PoweredByName) {
                this.PoweredByName = msg.PoweredByName;
            } // #88323
            if (msg.PoweredByURL) {
                this.PoweredByURL = msg.PoweredByURL;
            } // #88323
            if (msg.BrandingVisibleInstrumentLogo) {
                this.BrandingVisibleInstrumentLogo = msg.BrandingVisibleInstrumentLogo === '1';
            }
            this.AllowPreviewPassword = msg.BrandingAllowPasswordPreview === '1'; // #98204
        });
    }
    GetYieldRate(instrument) {
        const instrumentID = instrument.Id;
        const routeID = this.Routes.getRouteByName(instrument.getRoute()).RouteId;
        return Connection.vendor.GetYieldRate(instrumentID, routeID).then(function (msg) {
            if (!msg.length || msg[0].Code !== Message.CODE_YTM_RESPONSE) {
                return false;
            }
            if (msg[0].YieldRate) {
                instrument.YieldRate = msg[0].YieldRate;
            }
            return true;
        });
    }
    AddDeferredMessage(msg) {
        const route = msg.Route;
        const insTradableID = msg.InstrumentTradableID;
        if (!route || !insTradableID)
            return;
        const key = insTradableID + '::' + route;
        if (!this.deferredInsReq[key]) {
            this.deferredInsReq[key] = true;
            this.getInstrumentByInstrumentTradableID_NFL(insTradableID, route).then(function (instrument) {
                this.deferredInsReq[key] = false;
                if (!instrument) {
                    return;
                }
                this.ProcessDeferredMessages(instrument);
            }.bind(this));
        }
        if (!this.deferredMessages[route]) {
            this.deferredMessages[route] = {};
        }
        const deferredByRoute = this.deferredMessages[route];
        if (!deferredByRoute[insTradableID]) {
            deferredByRoute[insTradableID] = [];
        }
        deferredByRoute[insTradableID].push(msg);
    }
    ProcessDeferredMessages(instrument) {
        let deferredMsgArr = [];
        // this.SkipNewDefer = true;
        if (instrument === null) {
            const routes = Object.keys(this.deferredMessages);
            for (let i = 0; i < routes.length; i++) {
                const byRoute = this.deferredMessages[routes[i]];
                const byIns = Object.keys(byRoute);
                for (let j = 0; j < byIns.length; j++) {
                    deferredMsgArr = deferredMsgArr.concat(byRoute[byIns[j]]);
                }
            }
            this.deferredMessages = {};
        }
        else {
            const route = instrument.Route;
            const insTradableID = instrument.InstrumentTradableID;
            if (!route || !insTradableID)
                return;
            const deferredByRoute = this.deferredMessages[route];
            if (!deferredByRoute)
                return;
            const deferredByInstrument = deferredByRoute[insTradableID];
            if (!(deferredByInstrument === null || deferredByInstrument === void 0 ? void 0 : deferredByInstrument.length))
                return;
            deferredMsgArr = deferredByRoute[insTradableID].slice(0);
            delete deferredByRoute[insTradableID];
            if (Object.keys(deferredByRoute).length === 0) {
                delete this.deferredMessages[route];
            }
        }
        for (let i = 0; i < deferredMsgArr.length; i++) {
            // TODO заебашить норм в 3.99, то херь дикая как по мне 18.05.2021
            // переделать на менеджер этого всего
            let resMsgs = [];
            const msg = deferredMsgArr[i];
            if (msg.CPHFD) {
                resMsgs = msg.CPHFD(msg);
            }
            else {
                resMsgs = [msg];
            }
            for (let j = 0; j < resMsgs.length; j++) {
                this.NewMessage(resMsgs[j]);
            }
        }
    }
    GenerateFakeStrikes(result, im, ds, route, pointSize, generateStrikes) {
        // TODO: как только сервер дошлёт заменить на реальный пресижн
        const baseInstrPrecision = MathUtils.GetDecimalPlaces(pointSize, 20);
        const strikes = generateStrikes ? ds.StrikePricesList : [];
        // TODO его у нас нет
        if (strikes.length == 0) // для запроса опшионмастера
         {
            result.push(this.GenerateFakeStrike(im, ds, route, null, -1, -1, baseInstrPrecision, true));
        }
        for (let i = 0; i < strikes.length; i++) {
            const strike = strikes[i];
            const strikePrice = strike.StrikePrice;
            const strikeVersion = strike.StrikeVersion;
            if (strike.CallEnabled) {
                const optionInstr = this.GenerateFakeStrike(im, ds, route, strikeVersion, strikePrice, OptionPutCall.OPTION_CALL_VANILLA, baseInstrPrecision);
                if (optionInstr != null) {
                    result.push(optionInstr);
                }
            }
            if (strike.PutEnabled) {
                const optionInstr = this.GenerateFakeStrike(im, ds, route, strikeVersion, strikePrice, OptionPutCall.OPTION_PUT_VANILLA, baseInstrPrecision);
                if (optionInstr != null) {
                    result.push(optionInstr);
                }
            }
        }
    }
    GenerateFakeStrike(mess, ds, route, strikeVersion, strikeprice, putcall, baseinstrumentPrecision, OptionMasterLookupMode) {
        if (!strikeprice) {
            strikeprice = -1;
        }
        if (putcall === undefined || putcall === null) {
            putcall = -1;
        }
        if (!baseinstrumentPrecision) {
            baseinstrumentPrecision = 0;
        }
        if (!OptionMasterLookupMode) {
            OptionMasterLookupMode = false;
        }
        /// baseinstrumentPrecision ! Женя: в этом методе и выше (GenerateOptionInstrument) надо брать вариабл тик сайз если он установлен. когда протягивать то для интелисенса надо тоже!
        let underlierRoute = route;
        //
        // Fake
        const underlierName = ds.Underlier || mess.ForwardBaseInstrument;
        const underlierDescription = mess.ForwardBaseInstrumentDescr;
        const underlierCountryId = mess.ForwardBaseInstrumentCountryId;
        const underlierTradingExchange = mess.ForwardBaseInstrumentTradingExchange;
        // TODO NOrmal logic
        // if (ds.UnderlierDate)
        //     underlierName = ds.FutureAliasName;
        let baseInstr = this.getInstrumentByTradable_ID(ds.UnderlierTradableId, route);
        if (baseInstr == null) {
            const im_forBase = new DirectInstrumentMessage();
            im_forBase.Name = mess.ForwardBaseInstrument;
            im_forBase.RoutesSupported = mess.RoutesSupported; // route <- закомментил, а то IsHideRouteMode неправильно применялся
            im_forBase.TradableRoutes = mess.TradableRoutes; // #44590
            im_forBase.Exp2 = Instrument.FAKE_NONFIXED_RESPONSE;
            im_forBase.InstrType = ds.UnderlierDate ? InstrumentTypes.FUTURES : InstrumentTypes.INDICIES;
            im_forBase.SeriesGroupName = mess.SeriesGroupName;
            if (underlierDescription) {
                im_forBase.Descr = underlierDescription;
            }
            if (underlierCountryId) {
                im_forBase.CountryId = underlierCountryId;
            }
            if (underlierTradingExchange) {
                im_forBase.TradingExchange = underlierTradingExchange;
            }
            // #57604 - hsa: ранее мы использовали роут опциона при генерации андерлаера, но по факту это делать нельзя, т.к. у андерлаера может быть другой роут
            // нельзя генерировать фейковый андерлаер по неправильному роуту, если реальный андерлаер уже находится в агрегированных инструментах, т.к. в методе UpdateHideRouteMode апдейтится видимость инструментов на агрегированных инструментах
            const agrIns = this.getInstrumentByName(underlierName);
            // let agrIns = BaseApplication.App.MultiDataCache.GetAggregatedInstrument(underlierName);
            if (agrIns != null) {
                if (!agrIns.TradableRoutes.includes(underlierRoute) &&
                    !agrIns.RoutesSupported.includes(underlierRoute)) {
                    // не нашли нужный нам роут, берем первый (!) торговый (или первый информационный), это должно покрыть кейс, когда андерлаер и опцион находятся на разных роутах
                    if (agrIns.TradableRoutes.length > 0) {
                        underlierRoute = agrIns.TradableRoutes[0];
                    }
                    else if (agrIns.RoutesSupported.length > 0) {
                        underlierRoute = agrIns.RoutesSupported[0];
                    }
                }
                // нужно зааффектить InstrumentMessage, иначе неверно отработает метод UpdateHideRouteMode на инструменте
                im_forBase.RoutesSupported = agrIns.RoutesSupported.join(';');
                im_forBase.TradableRoutes = agrIns.TradableRoutes.join(';');
            }
            const baseInstrDs = new InstrDateSettings();
            if (ds.UnderlierTradableId) {
                baseInstrDs.InstrumentTradableID = ds.UnderlierTradableId;
            }
            if (im_forBase.InstrType == InstrumentTypes.FUTURES) {
                baseInstrDs.ContractMonthDate = ds.UnderlierDate;
                baseInstr = this.GenerateInstrument(underlierName, im_forBase, baseInstrDs, underlierRoute, true);
            }
            else {
                baseInstr = this.GenerateInstrument(underlierName, im_forBase, baseInstrDs, underlierRoute, true);
            }
            if (baseInstr == null) {
                return null;
            }
            // +++
            baseInstr.SourceName = mess.ForwardBaseInstrument;
        }
        // 3  кейса:
        // 1. опцион без страйков для начального заполнения лукапа
        // 2. заполнение опциона со страйками для StrikesResponse
        // 3. OptionMasterLookupMode - имя как у андерлаера
        let name;
        let aliasName = null;
        const optGenName = mess.Name;
        if (ds.useAliasName) {
            const s = ds.getStrikePriceSettings(strikeVersion, strikeprice);
            if (putcall == OptionPutCall.OPTION_CALL_VANILLA && s.CallEnabled) {
                aliasName = s.CallTicker;
            }
            else if (putcall == OptionPutCall.OPTION_PUT_VANILLA && s.PutEnabled) {
                aliasName = s.PutTicker;
            }
        }
        if (aliasName) {
            name = aliasName;
        }
        else {
            name = optGenName;
        }
        const instrument = this.GenerateInstrument(name, mess, ds, OptionMasterLookupMode ? underlierRoute : route, true);
        void this.CountryCache.fetchFlagIfNotAlready(instrument.CountryId);
        instrument.StrikeVersion = strikeVersion;
        instrument.StrikePrice = strikeprice;
        instrument.PutCall = putcall;
        const s = ds.getStrikePriceSettings(strikeVersion, strikeprice);
        if (s && putcall === OptionPutCall.OPTION_CALL_VANILLA && s.CallEnabled) {
            instrument.InstrumentTradableID = s.InstrumentTradableIDCall;
            if (isValidString(s.DescriptionCall)) {
                instrument.Description = s.DescriptionCall;
            }
        }
        else if (s && putcall === OptionPutCall.OPTION_PUT_VANILLA && s.PutEnabled) {
            instrument.InstrumentTradableID = s.InstrumentTradableIDPut;
            if (isValidString(s.DescriptionPut)) {
                instrument.Description = s.DescriptionPut;
            }
        }
        instrument.ForwardBaseInstrument = baseInstr;
        instrument.ForwardBaseInstruments = new Array(0);
        return instrument;
    }
    /// <summary>
    /// внутри обновляются инструмент поля
    /// </summary>
    UpdateDerInstr(der_instr, ds, mess) {
        if (der_instr == null || ds == null) {
            return;
        }
        der_instr.ContractID = ds.ContractID;
        der_instr.ExpDate = ds.ContractMonthDate;
        der_instr.ExpDateReal = ds.ExpirationDate;
        der_instr.DeliveryStatus = ds.DeliveryStatus;
        der_instr.ContractMonthDate = ds.ContractMonthDate;
        der_instr.LastTradeDate = ds.LastTradeDate;
        der_instr.SettlementDate = ds.SettlementDate;
        der_instr.NoticeDate = ds.NoticeDate;
        der_instr.FirstTradeDate = ds.FirstTradeDate;
        der_instr.AutoCloseDate = ds.AutoCloseDate;
        der_instr.Limits.HightLimit = ds.HightLimit;
        der_instr.Limits.LowLimit = ds.LowLimit;
        der_instr.Limits.PriceLimitMeasure = ds.PriceLimitMeasure;
        der_instr.Limits.IsHighLimitFrontEndValidationEnabled = ds.IsHighLimitFrontEndValidationEnabled;
        der_instr.Limits.IsLowLimitFrontEndValidationEnabled = ds.IsLowLimitFrontEndValidationEnabled;
        if (mess.Descr) {
            der_instr.Description = der_instr.SeriesDescription = mess.Descr;
        }
        if (ds.Description) {
            der_instr.Description = der_instr.SeriesDescription = ds.Description;
        }
        //
        // Trading halt
        //
        der_instr.TradingMode = ds.TradingMode;
        // #53213 Tick size/Tick cost/Lot step on contact level
        if (ds.LotSize !== undefined) {
            der_instr.LotSize = ds.LotSize;
        }
        if (ds.PointSize !== undefined) {
            der_instr.PointSize = ds.PointSize;
        }
        if (ds.LotStep !== undefined) {
            der_instr.LotStep = ds.LotStep;
        }
        if (ds.FuturesTickCoast !== undefined) {
            der_instr.FuturesTickCoast = ds.FuturesTickCoast;
        }
        if (ds.VariableTicks != null) {
            der_instr.VariableTickList = ds.VariableTicks;
        }
        else if (ds.PointSize &&
            ds.FuturesTickCoast) // не пришел переменный тиксайз, зато пришел тиксайз и тиккост, настроенный на серии, мы не имеем права брать переменный тиксайз из IM
         {
            der_instr.VariableTickList = [
                new VariableTick({
                    lowLimit: Number.NEGATIVE_INFINITY,
                    highLimit: Number.POSITIVE_INFINITY,
                    allowLimit: true,
                    pointSize: ds.PointSize,
                    tickCost: ds.FuturesTickCoast
                })
            ];
        }
        der_instr.AdditionalFields = ds.AdditionalFields;
        // hsa. мы должны дополнительно еще задать точность
        if (ds.PointSize !== undefined || ds.VariableTicks !== null) {
            const prec = Instrument.CalculatePrecision(ds.PointSize !== undefined ? ds.PointSize : 0, ds.VariableTicks !== null ? ds.VariableTicks : der_instr.VariableTickList);
            der_instr.Precision = prec;
        }
        der_instr.TradeSessionStatusId = ds.TradeSessionStatusId || -1;
        der_instr.ExchangeSessionName = ds.ExchangeSessionName;
        der_instr.InstrumentTradableID = ds.InstrumentTradableID;
        der_instr.MinLot = ds.minLot;
        der_instr.MaxLot = ds.maxLot;
        der_instr.MaxLotInCash = ds.maxLotInCash;
        der_instr.ISIN = ds.ISIN;
    }
    GenerateOptionInstrument(mess, ds, route, putcall, baseInstr, strikeSettings) {
        const strikeVersion = strikeSettings.StrikeVersion;
        const strikePrice = strikeSettings.StrikePrice;
        let optTicker = '';
        let lowLimit = 0;
        let highLimit = 0;
        let minLot = -1;
        let maxLot = -1;
        let maxLotInCash = -1;
        let priceLimitMeasure = PriceLimitMeasure.DISABLE;
        let description = '';
        let extFields = null;
        let tradeSessionStatusId;
        let exchangeSessionName;
        let InstrumentTradableID;
        let ISIN = '';
        let tradingMode;
        if (putcall === OptionPutCall.OPTION_PUT_VANILLA) {
            optTicker = strikeSettings.PutTicker;
            lowLimit = strikeSettings.LowLimitPut;
            highLimit = strikeSettings.HighLimitPut;
            minLot = strikeSettings.minLotPut;
            maxLot = strikeSettings.maxLotPut;
            maxLotInCash = strikeSettings.maxLotInCashPut;
            priceLimitMeasure = strikeSettings.PriceLimitMeasurePut;
            description = strikeSettings.DescriptionPut;
            extFields = strikeSettings.ExtFieldsPut;
            tradeSessionStatusId = strikeSettings.TradeSessionStatusIdPut;
            exchangeSessionName = strikeSettings.ExchangeSessionNamePut;
            InstrumentTradableID = strikeSettings.InstrumentTradableIDPut;
            ISIN = strikeSettings.ISINPut;
            tradingMode = strikeSettings.TradingModePut;
        }
        else {
            optTicker = strikeSettings.CallTicker;
            lowLimit = strikeSettings.LowLimitCall;
            highLimit = strikeSettings.HighLimitCall;
            minLot = strikeSettings.minLotCall;
            maxLot = strikeSettings.maxLotCall;
            maxLotInCash = strikeSettings.maxLotInCashCall;
            priceLimitMeasure = strikeSettings.PriceLimitMeasureCall;
            description = strikeSettings.DescriptionCall;
            extFields = strikeSettings.ExtFieldsCall;
            tradeSessionStatusId = strikeSettings.TradeSessionStatusIdCall;
            exchangeSessionName = strikeSettings.ExchangeSessionNameCall;
            InstrumentTradableID = strikeSettings.InstrumentTradableIDCall;
            ISIN = strikeSettings.ISINCall;
            tradingMode = strikeSettings.TradingModeCall;
        }
        const instrument = this.GenerateInstrument(optTicker, mess, ds, route);
        instrument.StrikeVersion = strikeVersion;
        instrument.StrikePrice = strikePrice;
        instrument.PutCall = putcall;
        instrument.Limits.LowLimit = lowLimit;
        instrument.Limits.HightLimit = highLimit;
        instrument.Limits.IsHighLimitFrontEndValidationEnabled = ds.IsHighLimitFrontEndValidationEnabled;
        instrument.Limits.IsLowLimitFrontEndValidationEnabled = ds.IsLowLimitFrontEndValidationEnabled;
        instrument.MinLot = minLot;
        instrument.MaxLot = maxLot;
        instrument.MaxLotInCash = maxLotInCash;
        instrument.Limits.PriceLimitMeasure = priceLimitMeasure;
        if (isValidString(description)) {
            instrument.Description = description;
        }
        else if (isValidString(ds.Description)) {
            instrument.Description = ds.Description;
        }
        else {
            instrument.Description = description;
        }
        instrument.AdditionalFields = extFields;
        // указываю явно базовый инструмент, поскольку для случая, когда фьючерсов больше 1го,
        // инструмент сам определиться не может - какой именно ему нужен как базовый (не знает конкретную ds.ContractMonthDate)
        instrument.ForwardBaseInstrument = baseInstr;
        instrument.ForwardBaseInstruments = new Array(0); // если потребуется поменять, не забудь пофиксить ParseOptionName, иначе подписка накроется
        instrument.TradeSessionStatusId = tradeSessionStatusId | -1;
        instrument.ExchangeSessionName = exchangeSessionName;
        instrument.InstrumentTradableID = InstrumentTradableID;
        instrument.ISIN = ISIN;
        instrument.TradingMode = tradingMode; // #115179
        return instrument;
    }
    RecalculateBalances() {
        if (!this.Loaded) {
            return;
        }
        const orderDict = this.Trading.OrderDict;
        const prevTable = this.InstrumentBalancerTable;
        this.InstrumentBalancerTable = {};
        const userBalanserTable = {};
        for (const pos of this.Trading.getAllPositions()) {
            const InsFullName = pos.Instrument.GetInteriorID();
            let pBal = this.InstrumentBalancerTable[InsFullName];
            if (!pBal) {
                pBal = new InstrumentBalancer(pos.Instrument);
                this.InstrumentBalancerTable[InsFullName] = pBal;
            }
            if (pos.Account != null) {
                let uBal = userBalanserTable[pos.Account.AcctNumber];
                if (!uBal) {
                    uBal = new UserBalancer(pos.Account);
                    userBalanserTable[pos.Account.AcctNumber] = uBal;
                }
                if (pos.lastPriceUpdated && !pos.isTradePosition) {
                    // #49087 - не считаем профит если не было котировок; #97626 - не учитываем трейдовую позицию для профита
                    const profit = pos.getGrossPnL(true);
                    uBal.Profit += pos.Instrument.OptionTradingStyle !== OptionTradingStyle.MarkToMarket ? profit : 0;
                    const netProfitInAccCur = pos.getNetPnL(true);
                    if (netProfitInAccCur !== null && uBal.ProfitNet !== null) {
                        uBal.ProfitNet += netProfitInAccCur;
                    }
                    else {
                        uBal.ProfitNet = null;
                    }
                    // uBal.ProfitForMargin += MarginCounterLocal.GetProfitForMargin(pos, profit);
                    const onlyProfit = MarginCounterLocal.GetProfitLossWithUnsettledProfitProperties(pos, profit > 0 ? profit : 0);
                    uBal.ProfitForUnsettled += onlyProfit;
                    const rs = pos.Instrument.RiskSettings;
                    if (rs !== null && rs.AllowWithdrawUnrealizedProfit(pos.ProductType, pos.Account) === false) {
                        uBal.OnlyProfitForWithdrawalAvailable += onlyProfit;
                    }
                    uBal.LossForUnsettled += MarginCounterLocal.GetProfitLossWithUnsettledProfitProperties(pos, profit < 0 ? profit : 0);
                    uBal.ProfitBase += pos.getGrossPnL(false);
                    const netProfitInBaseCur = pos.getNetPnL(false);
                    if (netProfitInBaseCur !== null && uBal.ProfitNetBase !== null) {
                        uBal.ProfitNetBase += netProfitInBaseCur;
                    }
                    else {
                        uBal.ProfitNetBase = null;
                    }
                }
                uBal.PositionsValue += pos.getPositionValue(true);
                if (!pos.isTradePosition) {
                    uBal.OpenPosNumber += 1;
                } // not counting trades number (FIFO) #80010
                uBal.OpenPosAmount += pos.Amount;
                if (this.isOrderBlockedForStocks(pos)) {
                    // #38604
                    uBal.BlockedForStocksAccountCurrency +=
                        pos.OpenPrice * pos.Amount * pos.Instrument.LotSize * pos.OpenCrossPrice;
                }
                //
                // +++ Asset support
                //
                if (pos.Account.AccountType == AccountType.MultiAsset && pos.Instrument != null) {
                    const assetName = Instrument.GetAssetNameCorrect(pos.Instrument, pos.BuySell == OperationType.Buy);
                    let userBalanceField = null;
                    userBalanceField = uBal.AssetBalances[assetName];
                    if (!userBalanceField) {
                        userBalanceField = new UserBalancesAssetBalanceFields();
                        uBal.AssetBalances[assetName] = userBalanceField;
                    }
                    // info
                    userBalanceField.OpenPosAmount += pos.Amount;
                    userBalanceField.OpenPosNumber += 1;
                }
                // PositionsValue
                if (pos.Instrument.LastQuote !== null && pos.Account !== null) {
                    const sp = this.GetSpreadPlan(pos.Account);
                    const lastPrice = pos.BuySell == OperationType.Buy
                        ? pos.Instrument.LastQuote.BidSpread_SP_Ins(sp, pos.Instrument)
                        : pos.Instrument.LastQuote.AskSpread_SP_Ins(sp, pos.Instrument);
                    const posVal = pos.Amount *
                        pos.Instrument.LotSize *
                        lastPrice *
                        pos.getCrossPrice() *
                        pos.Account.getCrossPrice();
                    let instrVal = 0;
                    if (uBal.DictPosInstrVal.hasOwnProperty(pos.Instrument)) {
                        instrVal = uBal.DictPosInstrVal[pos.Instrument];
                    }
                    if (pos.BuySell == OperationType.Buy) {
                        instrVal += posVal;
                    }
                    else {
                        instrVal -= posVal;
                    }
                    uBal.DictPosInstrVal[pos.Instrument] = instrVal;
                }
                uBal.positions.push(pos);
                uBal.totalPositionAmount += pos.Amount;
                uBal.NotIsOptionCount += 1;
            }
            pBal.Add(pos);
            if (pos.Account !== null &&
                pos.Amount !== 0 &&
                !pos.isTradePosition) // not counting trades amount (FIFO) #80010 #85827
             {
                const accNumber = pos.Account.AcctNumber;
                // Total position amount
                let totalPositionAmount = 0;
                if (pBal.AccountTotalPositionAmount.has(accNumber)) {
                    totalPositionAmount = pBal.AccountTotalPositionAmount.get(accNumber);
                }
                if (pos.BuySell === OperationType.Buy) {
                    totalPositionAmount += pos.Amount;
                }
                else if (pos.BuySell === OperationType.Sell) {
                    totalPositionAmount -= pos.Amount;
                }
                pBal.AccountTotalPositionAmount.set(accNumber, totalPositionAmount);
                // Total profit/loss
                let totalProfitLoss = 0;
                if (pBal.AccountTotalProfitLoss.has(accNumber)) {
                    totalProfitLoss = pBal.AccountTotalProfitLoss.get(accNumber);
                }
                // TODO PnL
                const val = pos.getGrossPnL(true);
                if (!isNaN(val) && isFinite(val)) {
                    totalProfitLoss += val;
                }
                pBal.AccountTotalProfitLoss.set(accNumber, totalProfitLoss);
                if (pos.BuySell === OperationType.Buy) {
                    let accountPositionPriceMultiplyAmountBuy = 0;
                    let accountPositionAmountBuy = 0;
                    if (pBal.AccountPositionPriceMultiplyAmountBuy.has(accNumber)) {
                        accountPositionPriceMultiplyAmountBuy =
                            pBal.AccountPositionPriceMultiplyAmountBuy.get(accNumber);
                    }
                    if (pBal.AccountPositionAmountBuy.has(accNumber)) {
                        accountPositionAmountBuy = pBal.AccountPositionAmountBuy.get(accNumber);
                    }
                    accountPositionPriceMultiplyAmountBuy += pos.Price * pos.Amount;
                    accountPositionAmountBuy += pos.Amount;
                    pBal.AccountPositionPriceMultiplyAmountBuy.set(accNumber, accountPositionPriceMultiplyAmountBuy);
                    pBal.AccountPositionAmountBuy.set(accNumber, accountPositionAmountBuy);
                }
                if (pos.BuySell === OperationType.Sell) {
                    let accountPositionPriceMultiplyAmountSell = 0;
                    let accountPositionAmountSell = 0;
                    if (pBal.AccountPositionPriceMultiplyAmountSell.has(accNumber)) {
                        accountPositionPriceMultiplyAmountSell =
                            pBal.AccountPositionPriceMultiplyAmountSell.get(accNumber);
                    }
                    if (pBal.AccountPositionAmountSell.has(accNumber)) {
                        accountPositionAmountSell = pBal.AccountPositionAmountSell.get(accNumber);
                    }
                    accountPositionPriceMultiplyAmountSell += pos.Price * pos.Amount;
                    accountPositionAmountSell += pos.Amount;
                    pBal.AccountPositionPriceMultiplyAmountSell.set(accNumber, accountPositionPriceMultiplyAmountSell);
                    pBal.AccountPositionAmountSell.set(accNumber, accountPositionAmountSell);
                }
                // qty для ордеров на sell
                let amountSellOrders = 0;
                for (const order of orderDict.values()) {
                    if (order.Active &&
                        order.BuySell === OperationType.Sell &&
                        order.Instrument === pos.Instrument &&
                        order.Account === pos.Account) {
                        amountSellOrders += order.AmountRemaining;
                    }
                }
                // TODO. Refactor.
                pos.AvailableForSell = amountSellOrders;
            }
            pos.RecalcAmounts();
        }
        // https://tp.traderevolution.com/entity/105525
        for (const position of this.Trading.PositionDictCorporateAction.values()) {
            position.RecalcAmounts();
        }
        //
        // Orders
        //
        for (const ord of this.Trading.getAllOrders()) {
            if (ord.Account != null &&
                (ord.Active || (ord.BoundTo && ord.BoundTo != '-1') || (ord.PositionId && ord.PositionId != '-1'))) {
                let uBal = userBalanserTable[ord.Account.AcctNumber];
                if (!uBal) {
                    uBal = new UserBalancer(ord.Account);
                    userBalanserTable[ord.Account.AcctNumber] = uBal;
                }
                uBal.OpenOrdAmount += ord.Amount;
                uBal.OpenOrdNumber += 1;
                //
                // +++ Asset support
                //
                if (ord.Account.AccountType == AccountType.MultiAsset && ord.Instrument != null) {
                    const assetName = Instrument.GetAssetNameCorrect(ord.Instrument, ord.BuySell == OperationType.Buy);
                    let userBalanceField = uBal.AssetBalances[assetName];
                    if (!userBalanceField) {
                        userBalanceField = new UserBalancesAssetBalanceFields();
                        uBal.AssetBalances[assetName] = userBalanceField;
                    }
                    // info
                    userBalanceField.OpenOrdAmount += ord.Amount;
                    userBalanceField.OpenOrdNumber += 1;
                }
                if (this.isOrderBlockedForStocks(ord)) // #38604
                 {
                    uBal.BlockedForStocksAccountCurrency +=
                        ord.Price * ord.Amount * ord.getCrossPrice() * ord.Account.getCrossPrice();
                }
            }
            if (ord.Account != null) {
                if (ord.Active && ord.Status !== OrderStatus.PENDING_NEW) {
                    const InsID = ord.Instrument.GetInteriorID();
                    let pBal = this.InstrumentBalancerTable[InsID];
                    if (!pBal) {
                        pBal = new InstrumentBalancer(ord.Instrument);
                        this.InstrumentBalancerTable[InsID] = pBal;
                    }
                }
            }
        }
        try {
            //
            // AlexB Optimization: внутри getTotalMargin()
            // каждый раз запрашивается список позиций. Получим его один раз и передадим как параметр
            //
            for (const acc in this.Accounts) {
                const account = this.Accounts[acc];
                let uBal = userBalanserTable[account.AcctNumber];
                // AlexB optimization: не будем создавать лишний объект, используем готовый с теми же нулевыми значениями
                if (uBal == null) {
                    uBal = this.EmptyUserBalancer;
                }
                //    userBalanserTable[account.AccountNumber] = (uBal = new UserBalancer(account));
                account.Profit = uBal.Profit;
                account.LossForUnsettled = uBal.LossForUnsettled;
                account.ProfitBase = uBal.ProfitBase;
                account.ProfitNet = uBal.ProfitNet;
                account.ProfitForMargin = uBal.ProfitForMargin;
                account.ProfitForUnsettled = uBal.ProfitForUnsettled;
                account.OnlyProfitForWithdrawalAvailable = uBal.OnlyProfitForWithdrawalAvailable;
                account.ProfitNetBase = uBal.ProfitNetBase;
                account.OpenOrdAmount = uBal.OpenOrdAmount;
                account.OpenPosAmount = uBal.OpenPosAmount;
                account.OpenPosNumber = uBal.OpenPosNumber;
                account.OpenOrdNumber = uBal.OpenOrdNumber;
                account.BlockedForStocksAccountCurrency = uBal.BlockedForStocksAccountCurrency;
                account.PositionsValue = uBal.PositionsValue;
                //
                // Asset support
                //
                if (account.AccountType == AccountType.MultiAsset) {
                    for (const assBal in account.assetBalances) {
                        const assBalance = account.assetBalances[assBal];
                        //
                        // !assBalance.IsEmpty - для оптимизации
                        // Андрей сказал, что это признак того, что нет ордеров/позиций
                        //
                        const userBalancesAssetFields = uBal.AssetBalances[assBalance.Asset.Name];
                        if (!assBalance.IsEmpty && userBalancesAssetFields) {
                            assBalance.OpenPosNumber = userBalancesAssetFields.OpenPosNumber;
                            assBalance.OpenOrdNumber = userBalancesAssetFields.OpenOrdNumber;
                        }
                        else {
                            assBalance.OpenPosNumber = 0;
                            assBalance.OpenOrdNumber = 0;
                        }
                    }
                }
                let profitForWA = account.Profit;
                if (profitForWA > 0) {
                    profitForWA = 0;
                }
                // +++ Пересчитать today balance
                if (uBal.positions.length > 0) {
                    account.StockValueByBasis = 0; // GetStockValue(account, StockValueMode.BeginBalance, uBal.positions);
                    account.StockValueWithoutPnl = 0; // GetStockValue(account, StockValueMode.StockValueWithoutPnl, uBal.positions);
                    account.StockValueDiscount = 0; // GetStockValue(account, StockValueMode.StockValueDiscount, uBal.positions);
                }
                else {
                    account.StockValueByBasis = 0;
                    account.StockValueWithoutPnl = 0;
                    account.StockValueDiscount = 0;
                }
                uBal.positions.length = 0;
                // users[count++] = account;
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
        }
        // clear  prevTable
        if (prevTable) {
            const IBKeys = Object.keys(prevTable);
            for (let i = 0, len = IBKeys.length; i < len; i++) {
                const bal = prevTable[IBKeys[i]];
                const instrument = bal.instrument;
                instrument.PositionsTotalInfo.Clear();
            }
        }
        const IBKeys = Object.keys(this.InstrumentBalancerTable);
        for (let i = 0, len = IBKeys.length; i < len; i++) {
            const bal = this.InstrumentBalancerTable[IBKeys[i]];
            const instrument = bal.instrument;
            instrument.PositionsTotalInfo.accountTotalPositionAmount = bal.AccountTotalPositionAmount;
            instrument.PositionsTotalInfo.accountTotalProfitLoss = bal.AccountTotalProfitLoss;
            instrument.PositionsTotalInfo.accountAveragePositionPriceWithAmountBuy =
                bal.AccountPositionPriceMultiplyAmountBuy;
            instrument.PositionsTotalInfo.accountAveragePositionPriceWithAmountSell =
                bal.AccountPositionPriceMultiplyAmountSell;
            instrument.PositionsTotalInfo.accountPositionAmountBuy = bal.AccountPositionAmountBuy;
            instrument.PositionsTotalInfo.accountPositionAmountSell = bal.AccountPositionAmountSell;
        }
        this.OnRecalculatedBalances.Raise();
    }
    isOrderBlockedForStocks(order) {
        return (order.Instrument != null &&
            (order.Instrument.InstrType === InstrumentTypes.CORPORATE ||
                order.Instrument.InstrType === InstrumentTypes.EQUITIES ||
                order.Instrument.InstrType === InstrumentTypes.FUND ||
                order.Instrument.InstrType === InstrumentTypes.ETF) &&
            !order.Instrument.CFD);
    }
    AccountOperationRequest(data) {
        return Connection.AccountOperationRequest(data);
    }
    TryGetCrossratesPlan(id) {
        return this.CrossratesPlans.GetPlanById(id);
    }
    TryGetRiskPlan(id) {
        return this.riskPlanDict[id] || null;
    }
    TryGetSpreadPlan(id) {
        return this.SpreadPlans[id] || null;
    }
    TryGetCommissionPlan(id) {
        return this.CommissionPlans[id] || null;
    }
    TryGetInterestPlan(id) {
        return this.InterestsOnBalancePlans.get(id) || null;
    }
    TryGetSwapPlan(id) {
        return null; /* TODO */
    }
    TryGetChargingPlan(id) {
        return null; /* TODO */
    }
    getInstrumentsByInstrIdRouteIdFromCache(instrumentId, route) {
        let instruments = this.GetInstrumentsByInstrumentIdRouteId(instrumentId, route);
        if (instruments.length === 0) {
            instruments = this.GetInstrumentsByInstrumentId(instrumentId);
        }
        return instruments;
    }
    FindTradableContinuousContract(instrumentId, route) {
        let instruments = this.getInstrumentsByInstrIdRouteIdFromCache(instrumentId, route);
        instruments = instruments.filter((instrument) => {
            var _a;
            const isContinuousContract = instrument.InstrumentSpecificType === InstrumentSpecificType.ContinuousContract;
            if (isContinuousContract) {
                const routeObj = this.Routes.getRouteById(instrument.Route);
                return (_a = routeObj === null || routeObj === void 0 ? void 0 : routeObj.IsTradable) !== null && _a !== void 0 ? _a : false;
            }
            return false;
        });
        return instruments.length > 0 ? instruments[0] : null;
    }
    FindUnderlier(instrumentId, route, underlierTradableId = '') {
        let instruments = this.getInstrumentsByInstrIdRouteIdFromCache(instrumentId, route);
        instruments = instruments.filter((instrument) => instrument.InstrumentSpecificType !== InstrumentSpecificType.ContinuousContract);
        if (isValidString(underlierTradableId)) {
            instruments = instruments.filter((instrument) => instrument.InstrumentTradableID.toString() === underlierTradableId);
            if (instruments.length === 0) {
                return null;
            }
        }
        const firstInstrument = instruments[0];
        instruments = instruments.filter((instrument) => instrument.RouteIsTradable());
        if (instruments.length > 0) {
            return instruments[0];
        }
        else {
            return firstInstrument;
        }
    }
    GenerateFakeUnderlier(option, id, tradableId, name, isFutures) {
        const im = option.CreateInstrumentMessage();
        im.Id = id.toString();
        im.InstrumentTradableID = tradableId;
        im.Name = name;
        im.InstrType = isFutures ? InstrumentTypes.FUTURES : InstrumentTypes.GENERAL;
        const fakeUnderlier = new Instrument(option.DataCache, im, option.Route);
        return fakeUnderlier;
    }
    getInstruments(isOptionMasterMode) {
        const instruments = this.OptionsCache.getOptionInstruments(isOptionMasterMode ? OptionsFilter.All : OptionsFilter.Fake);
        if (!isOptionMasterMode) {
            for (const key of Object.keys(this.Instruments)) {
                instruments[key] = this.Instruments[key];
            }
        }
        return instruments;
    }
    GetInstrumentsByInstrumentId(id) {
        const instruments = this.InstrumentsByIdCache.get(id);
        if (isNullOrUndefined(instruments)) {
            return [];
        }
        return instruments;
    }
    GetInstrumentsByInstrumentIdRouteId(id, route) {
        return this.GetInstrumentsByInstrumentId(id).filter((instrument) => instrument.Route === route);
    }
    GetInstrumentsByContractID(id) {
        // return insArr where ins.ContractID == id, where id is method argument
        if (this._cachedInsListByContractID[id]) {
            return this._cachedInsListByContractID[id];
        }
        const allIns = this.Instruments;
        for (const insID in allIns) {
            const ins = allIns[insID];
            if (ins.ContractID == id) {
                if (!this._cachedInsListByContractID[id]) {
                    this._cachedInsListByContractID[id] = [];
                }
                this._cachedInsListByContractID[id].push(ins);
            }
        }
        return this._cachedInsListByContractID[id];
    }
    userHasSameCommissionAndSwapPlanOnAllAccounts() {
        if (this._userHasSameCommissionAndSwapPlanOnAllAccounts === null) {
            const accArr = Object.values(this.Accounts);
            const commissionPlanIds = accArr.map((account) => account.CommisionId);
            const swapPlanIds = accArr.map((account) => account.SwapPlanId);
            this._userHasSameCommissionAndSwapPlanOnAllAccounts =
                ArrayUtils.HasUniformValue(commissionPlanIds) && ArrayUtils.HasUniformValue(swapPlanIds);
        }
        return this._userHasSameCommissionAndSwapPlanOnAllAccounts;
    }
    userHasSameCurrencyOnAllAccounts() {
        return Boolean(this._userHasSameCurrencyOnAllAccounts);
    }
    cacheUserHasSameCurrencyOnAllAccounts() {
        const baseCurrencies = Object.values(this.Accounts).map((account) => account.BaseCurrency);
        const uniqueCurrencies = new Set(baseCurrencies);
        this._userHasSameCurrencyOnAllAccounts = uniqueCurrencies.size === 1;
    }
    getUserBaseCurrency() {
        // правила получения валюты аккаунта: https://ibb.co/HVtD0gw
        const accs = this.Accounts;
        const accKeys = Object.keys(accs);
        let currency = '';
        if (accKeys.length == 1 && accs[accKeys[0]].AccountType === AccountType.MultiAsset) {
            return this.baseCurrency;
        } // пункт 2 выполняется тут
        for (let i = 0; i < accKeys.length; i++) {
            const accCurrency = accs[accKeys[i]].BaseCurrency;
            if (currency.length && accCurrency != currency) // у юзера несколько аккаунтов с разными валютами
             {
                const positions = this.Trading.getAllPositions();
                currency = '';
                for (const position of positions) {
                    const positionCurrency = position.Account.BaseCurrency;
                    if (currency.length && positionCurrency != currency) {
                        return this.baseCurrency;
                    } // пункт 4 (у юзера несколько аккаунтов с разными валютами и позиции открыты в разных валютах -> вовзвращем валюту сервера)
                    else {
                        currency = positionCurrency;
                    }
                }
                // на случай отсутствия позиций
                return currency || this.baseCurrency; // пункт 5 выполняется тут (все позиции открыты в одной валюте ее и возвращаем)
            }
            else {
                currency = accCurrency;
            }
        }
        return currency; // пункт 1,3 выполняется тут
    }
    ProcessBrokerMessage(msg) {
        if (!msg)
            return;
        const histBM = new HistoricalBM();
        histBM.UpdateByBM(msg);
        this.OnProcessBrokerMessage.Raise(histBM);
        if (this.Loaded && msg.ShowPopUp === false) {
            this.OnBMCounterMsgReceived.Raise(msg.ID, NotificationsCounterChangedEvent.AddOne);
            return; // Приходит 51 месседж с POP_UP = false, сразу же кладем его в панель Notifications как непрочитанный и увеличиваем счетчик. TerceraBrokerMessageScreen - не отображаем
        }
        if (msg.ShowPopUp !== false) {
            brokerMessageScreenHandler.Show(msg);
        }
    }
    SendChangeExerciseStatusRequest(positionID, status) {
        Connection.vendor.SendExerciseRequest(positionID, status);
    }
    ExerciseOptionsByPosition(position, placedFrom) {
        if (!(position === null || position === void 0 ? void 0 : position.PositionId))
            return;
        this.GenerateExerciseRequestMessageDealTicket(position, false, placedFrom);
        return this.SendChangeExerciseStatusRequest(position.PositionId, Position.OPTION_EXERCISE_STATUS.PENDING_EXERCISE);
    }
    CancelExerciseByPosition(position, placedFrom) {
        if (!(position === null || position === void 0 ? void 0 : position.PositionId))
            return;
        this.GenerateExerciseRequestMessageDealTicket(position, true, placedFrom);
        return this.SendChangeExerciseStatusRequest(position.PositionId, Position.OPTION_EXERCISE_STATUS.EXERCISED);
    }
    SendMarginRequest(parameters) {
        return __awaiter(this, void 0, void 0, function* () {
            const marginRequestParameters = [];
            for (let i = 0; i < parameters.length; i++) {
                const marginInfoParameter = parameters[i];
                const marginRequestPrameter = {
                    mode: MarginRequestMode.All,
                    accountId: marginInfoParameter.account.userPin,
                    instrumentID: marginInfoParameter.instrument.InstrumentTradableID,
                    routeID: marginInfoParameter.instrument.Route,
                    operation: marginInfoParameter.operation,
                    amount: marginInfoParameter.amountLots,
                    cash: marginInfoParameter.cash,
                    isCashMode: marginInfoParameter.isCashMode,
                    price: marginInfoParameter.price,
                    stopPrice: marginInfoParameter.stopPrice,
                    orderType: marginInfoParameter.orderType,
                    validity: marginInfoParameter.validity,
                    productType: marginInfoParameter.productType,
                    leverage: marginInfoParameter.leverageValue,
                    isAllowOTHTrading: marginInfoParameter.isAllowOTHTrading
                };
                marginRequestParameters.push(marginRequestPrameter);
            }
            const response = yield Connection.vendor.SendMarginRequest(marginRequestParameters);
            return response[0];
        });
    }
    SendAccountFollowerRequest(args) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield Connection.vendor.SendAccountFollowerRequest(args);
        });
    }
    GetWatchListDefaultItem() {
        return { name: Resources.getResource('editableComboBox.defaultList'), instrDescriptions: [] };
    }
    SendDefaultAndCustomListRequest() {
        return __awaiter(this, void 0, void 0, function* () {
            // wait for main account
            let userID;
            const startTime = Date.now();
            let endTime;
            do {
                userID = this.getUserID();
                endTime = Date.now();
                if (isNullOrUndefined(userID)) {
                    yield delay(50);
                }
            } while (isNullOrUndefined(userID) && endTime - startTime < 10 * 1000);
            if (isNullOrUndefined(userID)) {
                console.warn('DataCache::SendDefaultAndCustomListRequest::Default and custom list request failed. User ID is not received.');
                return;
            }
            // Send requests and wait for responses
            const defaultListPromise = Connection.vendor.SendCustomListRequest(userID, CustomInstrumentListTypeEnum.DefaultList);
            const customListPromise = Connection.vendor.SendCustomListRequest(userID, CustomInstrumentListTypeEnum.CustomList);
            const byTypeMsgArr = yield Promise.all([defaultListPromise, customListPromise]);
            byTypeMsgArr
                .flatMap((msgArr) => msgArr)
                .forEach((msg) => {
                this.NewMessage(msg);
            });
            const itemsValue = [];
            const defaultListArray = this.GetCustomListsByType(CustomInstrumentListTypeEnum.DefaultList);
            if ((defaultListArray === null || defaultListArray === void 0 ? void 0 : defaultListArray.length) > 1) {
                itemsValue.push(this.GetWatchListDefaultItem()); // When a client receives a few “Default list(WL)” - needs to show 1 empty list with the name “Default list”. https://docs.google.com/document/d/13jvy4tGxqL7sR8pfYTnEQjDu_fwJ4oM6a7sRGPuT2pQ/edit#bookmark=id.p1qc4vclwl77
            }
            else {
                for (let i = 0; i < defaultListArray.length; i++) {
                    const defaultListItem = yield this.handleCustomInstrListForWatchList(defaultListArray[i]);
                    itemsValue.push(defaultListItem);
                }
            }
            const customListArray = this.GetCustomListsByType(CustomInstrumentListTypeEnum.CustomList);
            for (let i = 0; i < customListArray.length; i++) {
                const customListItem = yield this.handleCustomInstrListForWatchList(customListArray[i]);
                itemsValue.push(customListItem);
            }
            return itemsValue;
        });
    }
    ProcessTradingSystemMessage(msg) {
        if (!msg) {
            return;
        }
        const tradingSystem = new TradingSystem(msg);
        const id = tradingSystem.tradingSystemID;
        let needUpdateRiskDisclosureText = false; // для уже подтвержденных или новых тор.систем придет SubscriptionStrategyMessage по которому произойдет обновление текста, но в случае изменения текста RD неподтвержденной системы нужно обновить текст
        if (this.tradingSystems[id]) {
            const oldTradingSystemData = this.tradingSystems[id];
            needUpdateRiskDisclosureText =
                !oldTradingSystemData.accepted &&
                    oldTradingSystemData.riskDisclosureText != tradingSystem.riskDisclosureText;
        }
        this.tradingSystems[id] = tradingSystem;
        // this.GenerateDealTicketFromTradingIdea(tradingSystem, true)  // commented out due to #116976
        if (needUpdateRiskDisclosureText) {
            this.tradingSystems[id].accepted = false;
            this.OnTradingSystemsChange.Raise(id);
        }
    }
    ProcessSubscriptionStrategyMessage(msg) {
        if (!msg) {
            return;
        }
        const tradingSystems = this.tradingSystems;
        const trSystemID = msg.TradingSystemID;
        const trSystemStatusAccepted = msg.StrategySubscriptionStatus == StrategySubscriptionStatus.Accepted;
        const trSystemStatusDeleted = msg.StrategySubscriptionStatus == StrategySubscriptionStatus.Deleted;
        if (!(tradingSystems === null || tradingSystems === void 0 ? void 0 : tradingSystems[trSystemID])) {
            return;
        }
        if (trSystemStatusDeleted) {
            this.DeleteTradingSystem(trSystemID);
        }
        else {
            tradingSystems[trSystemID].accepted = trSystemStatusAccepted;
        }
        this.OnTradingSystemsChange.Raise();
    }
    ProcessTradingSystemRemoveMessage(msg) {
        if (!msg || msg.ID === null) {
            return;
        }
        this.DeleteTradingSystem(msg.ID);
    }
    DeleteTradingSystem(trSystemID) {
        const tradingSystems = this.tradingSystems;
        if (!(tradingSystems === null || tradingSystems === void 0 ? void 0 : tradingSystems[trSystemID])) {
            return;
        }
        delete this.tradingSystems[trSystemID];
        this.OnTradingSystemUnsubscribe.Raise(trSystemID);
    }
    ProcessCustodialPlanMessage(msg) {
        this.CustodialPlans.Add(msg);
    }
    ProcessCrossratesPlanMessage(msg) {
        this.CrossratesPlans.Add(msg);
    }
    ProcessFundingRateMarkupPlanMessage(msg) {
        this.FundingRateMarkupPlans.Add(msg);
    }
    ProcessRiskRuleWarningMessge(msg) {
        const header = DirectRiskWarningMessage.RiskRuleWarningHeader;
        const userTitle = Resources.getResource('dealticket.RiskRuleWarning.UserName');
        const accTitle = Resources.getResource('dealticket.RiskRuleWarning.AccountName');
        const message = Resources.getResource('AdditionalProperty.Message');
        if (!msg.AccountID) {
            return;
        }
        const acc = this.GetAccountByNumber(msg.AccountID.toString());
        const newMsg = new DirectReportMessage();
        newMsg.Name = header;
        if (acc !== null) {
            newMsg.Data = new Array(3);
            newMsg.Data[0] = [userTitle, acc.userLogin];
            newMsg.Data[1] = [accTitle, acc.FullAccString];
        }
        newMsg.Data[2] = [message, msg.Text];
        newMsg.TypeId = msg.RiskRuleType;
        if (newMsg.TypeId === DirectRiskWarningMessage.PositionLossLimitRiskRuleType) {
            const strs = msg.Text.match(/\d+/);
            let posId = '';
            if (strs === null || strs === void 0 ? void 0 : strs.length) {
                posId = strs[0];
            }
            msg.Text = Resources.getResource('AdditionalProperty.Position closed because the loss limit was reached').replace('{0}', posId);
            newMsg.Data[2] = [message, msg.Text];
            if (!isNullOrUndefined(riskWarningMessageScreenHandler) &&
                !isNullOrUndefined(riskWarningMessageScreenHandler.Show)) {
                riskWarningMessageScreenHandler.Show(newMsg);
            }
        }
        if (newMsg.TypeId === DirectRiskWarningMessage.NegativeProjectedBalanceRiskRuleType) {
            const msgText = Resources.getResource('RiskRuleType.NegativeProjectedBalance')
                .replace('{0}', acc.userLogin)
                .replace('{1}', acc.FullAccString);
            newMsg.Data[2][1] = msgText;
        }
        if (newMsg.TypeId === DirectRiskWarningMessage.AvailableCashRiskRuleType) {
            const msgText = Resources.getResource('RiskRuleType.AvailableCash');
            newMsg.Data[2][1] = msgText;
        }
        this.OnRiskRuleWarningMessage.Raise(newMsg);
        this.HandleReportMessageDirect(newMsg);
    }
    SendSubscriptionStrategyRequest(tradingSystemID) {
        Connection.vendor.SendSubscriptionStrategyRequest(tradingSystemID);
    }
    ProcesSubscribeResponseMessage(msg) {
        if (!msg)
            return;
        const id = msg.InstrumentId;
        const route = msg.Route;
        const ins = id && route ? this.getInstrument(String(id), route) : null;
        if (ins) {
            const delay = msg.Delay;
            const canGetSnapshot = msg.CanGetSnapshot;
            const hasLvl1SubscriptionErrorCode = msg.ErrorCode !== null;
            ins.updateDelayAndCanGetSnapshot(delay, canGetSnapshot, hasLvl1SubscriptionErrorCode, msg.Service);
            const subscriptionItem = this.FQuoteCache.getSubscriptionItem(ins);
            if (subscriptionItem === null || subscriptionItem === void 0 ? void 0 : subscriptionItem.HasContinuousContractItem) {
                const continuousContractItem = subscriptionItem.InstrumentContinuousContractItem;
                if (continuousContractItem === null || continuousContractItem === void 0 ? void 0 : continuousContractItem.instrument) {
                    continuousContractItem.instrument.updateDelayAndCanGetSnapshot(delay, canGetSnapshot, hasLvl1SubscriptionErrorCode, msg.Service);
                } // #100762 comment
            }
        }
    }
    SendSnapshotRequestMessageAsync(tradableID, routeID) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield Connection.vendor.SendSnapshotRequestMessageAsync(tradableID, routeID);
            if (isValidArray(result) && result[0].Code === Message.CODE_GET_SNAPSHOT_RESPONSE) {
                return result[0];
            }
            return null;
        });
    }
    getAvailableNumberOfSnapshotRequests() {
        return this.getRuleNumberValueForAccount(RulesSet.AVAILABLE_NUMBER_OF_SNAPSHOT_REQUESTS, null, 0);
    }
    ProcessCommissionPlanMessage(msg) {
        const commissionPlan = new CommissionPlan(this);
        commissionPlan.Update(msg);
        this.CommissionPlans[msg.Id] = commissionPlan;
        this.UpdateAccountsCommissionPlan();
        this.OnCommissionPlanUpdate.Raise(msg.Id);
    }
    SendBrokerMessageReport(inputMessage) {
        // закоментовано, згодом відновлено згідно з #110904 (для urgent BM було додано логіювання у #84587, судячи з усього зараз ця логіка вже є застарілою і непотрібною)
        Connection.vendor.SendBrokerMessageReport(inputMessage, TextMessageType.STATUS_CHANGE);
    }
    SendBrokerResponseMessage(params) {
        const { brokerMsgID, value, userID, userName, responseType, clusterNode } = params;
        if (responseType === BrokerMessageResponseType.WAS_READ) {
            this.OnBMCounterMsgReceived.Raise(brokerMsgID, NotificationsCounterChangedEvent.RemoveOne);
        }
        return Connection.vendor.SendBrokerResponseMessage(brokerMsgID, value, userID, userName, responseType, clusterNode);
    }
    SendBrokerMessageHistoryRequest(userID) {
        return Connection.vendor.SendBrokerMessageHistoryRequest(userID);
    }
    ProcessBMCounterMessage(msg) {
        if (msg && !MathUtils.IsNullOrUndefined(msg.UnreadIDs)) {
            this.OnBMCounterMsgReceived.Raise(msg.UnreadIDs);
        }
    }
    ProcessPriceLimitsMessage(msg) {
        if (!msg) {
            return;
        }
        const insArr = this.getInstrumentsByTradableID(msg.TradableId);
        for (let i = 0; i < insArr.length; i++) {
            const ins = insArr[i];
            if (msg.HighLimit !== null) {
                ins.Limits.HightLimit = msg.HighLimit;
            }
            if (msg.LowLimit !== null) {
                ins.Limits.LowLimit = msg.LowLimit;
            }
        }
    }
    SendInformationMessage(storage) {
        for (let i = 0; i < storage.length; i++) {
            if (Connection === null || Connection === void 0 ? void 0 : Connection.vendor) {
                Connection.vendor.SendInformationMessage(storage[i]);
            }
        }
    }
    handleOrderHistoryMessage(msg) {
        this.FOrderHistoryCache.newMessage(msg);
    }
    RemovedTradeHistory(ordId) {
        const arr = this.filledOrdersArray;
        let i = 0;
        while (arr[i]) {
            if (arr[i].OrderId === ordId) {
                arr.splice(i, 1);
            }
            else {
                i++;
            }
        }
    }
    getOrderHistoryArray() {
        return this.FOrderHistoryCache.orderHistoryArray;
    }
    ReSubscribe() {
        for (const order of this.Trading.getAllOrders()) {
            order.SubscribeToQuotes();
        }
        for (const position of this.Trading.getAllPositions()) {
            position.SubscribeToQuotes();
        }
        // https://tp.traderevolution.com/entity/105525
        for (const position of this.Trading.getAllPositionsCorporateAction()) {
            position.SubscribeToQuotes();
        }
    }
    /// <summary>
    /// Поиск ассетбаланса, правильный его пользовать везде
    /// </summary>
    GetAssetBalanceCorrect(account, instrument, isBuy) {
        if (account === null) {
            return null;
        }
        return account.GetAssetBalanceCorrect(instrument, isBuy);
    }
    ProcessSourceMappingMessage(msg) {
        this.SourceCollection.UpdateByMessage(msg);
    }
    GetSourceName(id) {
        return this.SourceCollection.GetSourceName(id);
    }
    GetDataSourceInstrument(instrument) {
        if (!instrument) {
            return null;
        }
        if (instrument.DataSourceInstrument) {
            return instrument.DataSourceInstrument;
        }
        const InstrumentId = instrument.InstrumentTradableID;
        const route = instrument.Route;
        let dataSourceTrInsID = instrument.GetDataSourceTradableId();
        let dataSourceRouteID = instrument.GetDataSourceRouteId();
        if (dataSourceTrInsID == -1) {
            dataSourceTrInsID = InstrumentId;
        }
        if (dataSourceRouteID == -1) {
            dataSourceRouteID = route;
        }
        const dsInteriorID = dataSourceTrInsID + InstrumentUtils.SEPARATOR + dataSourceRouteID;
        const dsIns = this.Instruments[dsInteriorID] || null;
        if (dsIns) {
            instrument.DataSourceInstrument = dsIns;
        } // кэшируем
        return dsIns;
    }
    GetInstrumentsByDataSource(instrument) {
        if (!instrument) {
            return null;
        }
        const interiorID = instrument.GetInteriorID();
        return this.InstrumentsBySourceRouteIdTradableId[interiorID] || [];
    }
    SendDataSourceSubscriptionMessage(instrument, toSubscribe, quoteType) {
        if (!(instrument === null || instrument === void 0 ? void 0 : instrument.IsDataSourceEnable())) {
            return;
        }
        const dsIns = this.GetDataSourceInstrument(instrument);
        const InstrumentId = instrument.InstrumentTradableID;
        const dataSourceTrInsID = dsIns.InstrumentTradableID;
        const dataSourceRouteID = dsIns.Route;
        const subscriptionActionType = toSubscribe ? SubscribeType.SUBSCRIBE : SubscribeType.UNSUBSCRIBE;
        if (Connection.vendor) // эта проверка необходима для работы Unit-тестов
         {
            Connection.vendor.SendDataSourceSubscriptionMessage(dataSourceTrInsID, dataSourceRouteID, quoteType, subscriptionActionType, InstrumentId);
        }
    }
    IsPortfolioAllowed(account) {
        if (!account) {
            return false;
        }
        return this.isAllowedForAccount(RulesSet.FUNCTION_PORTFOLIO_MANAGEMENT, account);
    }
    IsPortfolioAllowedForAnyAccount() {
        const pCache = this.PortfolioCache;
        const accs = this.getAccounts();
        for (const accID in accs) {
            if (this.IsPortfolioAllowed(accs[accID]) && pCache.IsInvestorAccount(accID)) {
                return true;
            }
        }
        return false;
    }
    AllAccountsAreInvestors() {
        const pCache = this.PortfolioCache;
        const accs = this.getAccounts();
        if (Object.keys(accs).length === 0) {
            return false;
        }
        for (const accID in accs) {
            if (!this.IsPortfolioAllowed(accs[accID]) || !pCache.IsInvestorAccount(accID)) {
                return false;
            }
        }
        return true;
    }
    NeedRedirectToPortfolio() {
        // возвращает true или false в зависимости от того произошел ли редирект на портфолио
        if (this.AllAccountsAreInvestors()) {
            LocalStorage.setTokenForPortfolio(Connection.vendor.loginParams.accessToken);
            window.location.href = window.location.href + 'myportfolio';
            return true;
        }
        return false;
    }
    AllowCustomSLTPTriggerPriceForUser() {
        // #109798
        return this.isAllowedForMyUser(RulesSet.FUNCTION_USER_ALLOW_CUSTOM_SLTP_TRIGGER_PRICE);
    }
    IsAllowSLTPPercentForUser() {
        return this.isAllowedForMyUser(RulesSet.FUNCTION_ALLOW_SLTP_IN_PERCENT);
    }
    AllowCustomSLTPTriggerPriceForInstrumentType(insType = null) {
        // #109798
        if (insType == null) {
            return false;
        }
        const instrumentTypesList = [];
        this.FindInstrumentTypes(insType, instrumentTypesList);
        for (const key in this.Instruments) {
            const ins = this.Instruments[key];
            if (ins.IsAllowCustomSLTPTriggerPrice && instrumentTypesList.includes(ins.InstrType)) {
                return true;
            }
        }
        return false;
    }
    AllowLoggingChanges() {
        // #111072
        return this.isAllowedForMyUser(RulesSet.ALLOW_CHANGES_LOGGING);
    }
    GetAllInstrumentsExceptExpired() {
        // return object like this.Instruments but with no options whose expiration date is in the past of all  #113208
        const result = {};
        const allIns = this.Instruments;
        for (const id in allIns) {
            const ins = allIns[id];
            if (!ins.ExpDate || ins.ExpDateReal.getTime() >= Date.now()) {
                result[id] = ins;
            }
        }
        return result;
    }
    GenerateExerciseRequestMessageDealTicket(pos, isCancelRequest, placedFrom) {
        if (!pos) {
            return;
        }
        const account = {
            Label: Resources.getResource('reports.Account'),
            Value: pos.Account ? pos.Account.FullAccString : null
        };
        const ins = {
            Label: Resources.getResource('InstrumentDetailsPanel.Symbol'),
            Value: pos.Instrument ? pos.Instrument.DisplayName() : null
        };
        const posID = {
            Label: Resources.getResource('reports.posId'),
            Value: pos.PositionId ? pos.PositionId : null
        };
        // let date = {
        //     Label: Resources.getResource('screen.reports.reportHeaders.date'),
        //     Value: Resources.getResource('reports.' + Utils.getKeyByValue(OrderExecutor.PlacedFrom, placedFrom))
        // }
        const placedFromData = {
            Label: Resources.getResource('reports.Send from'),
            Value: Resources.getResource('reports.' + PlacedFrom[placedFrom])
        };
        const msgRows = [account, ins, posID, placedFromData];
        const len = msgRows.length;
        const resultDataArr = [];
        for (let i = 0; i < len; i++) {
            const lbl = msgRows[i];
            if (lbl && (lbl.Value || lbl.Value === 0)) {
                resultDataArr.push([lbl.Label, lbl.Value]);
            }
        }
        const header = Resources.getResource(isCancelRequest
            ? 'screen.PositionExerciseCancelDealticket.header'
            : 'screen.PositionExerciseDealticket.header');
        const reportMessage = new DirectReportMessage(header, resultDataArr);
        reportMessage.SkipOnTicketViewer = true; // чтобы не показывать в TicketViewer
        this.NewMessage(reportMessage);
    }
    isAllowedMutualCloseByPosIdArr(positionsIDArray) {
        // #115483 Mutual closing can be carried out when two positions for the same instrument are selected in the Positions panel.
        if (!positionsIDArray || positionsIDArray.length !== 2) {
            return false;
        } // Two positions must be selected;
        const pos1 = this.Trading.getPositionById(positionsIDArray[0]);
        const pos2 = this.Trading.getPositionById(positionsIDArray[1]);
        if (pos1.Instrument != pos2.Instrument) {
            return false;
        } // Positions to close must be selected for the same instrument;
        if (pos1.BuySell == pos2.BuySell) {
            return false;
        } // These positions must be oppositely directed (i.e. both Long and Short positions must be selected);
        if (pos1.Account != pos2.Account) {
            return false;
        } // All positions must belong to the same account.
        const partialCloseIsForbidden = !this.isAllowedForAccount(RulesSet.FUNCTION_PARTIAL_CLOSE, pos1.Account);
        if (partialCloseIsForbidden && pos1.Amount != pos2.Amount) {
            return false;
        } // In case the partial close is forbidden for this account, then the amount on both sides (Long and Short) must be equal.
        return true;
    }
    PositionExerciseStatusChanged(posId) {
        if (messageBoxHandler === null || messageBoxHandler === void 0 ? void 0 : messageBoxHandler.Show) {
            const position = this.Trading.getPositionById(posId);
            let infoText = Resources.getResource('screen.PositionExerciseComplete.text');
            infoText = infoText.replace('{1}', position.PositionId).replace('{2}', position.Account.FullAccString);
            const addData = {
                cantActionBeforeClose: true,
                hideCloseBtn: true,
                okText: Resources.getResource('general.messageBox.ok')
            }; // #94234
            messageBoxHandler.Show(Resources.getResource('screen.PositionExerciseComplete.header'), infoText, messageBoxHandler.msgType.Info, null, null, false, true, null, addData);
        }
        else {
            this.OnPositionExerciseStatusChanged.Raise(posId);
        }
    }
    SendCloseAccountRequestMessage() {
        return Connection.vendor.SendCloseAccountRequestMessage();
    }
    SendCloseAccountCancelRequestMessage(closeAccountRequestID) {
        return Connection.vendor.SendCloseAccountCancelRequestMessage(closeAccountRequestID);
    }
    ProcessCloseAccountResponseMessage(msg) {
        const oldCloseAccountRequestID = this.closeAccountRequestID;
        switch (msg.status) {
            case CloseAccountResponseStatus.New:
                this.closeAccountRequestID = msg.closeAccountRequestID;
                break;
            case CloseAccountResponseStatus.Approve:
                this.closeAccountApproved = true;
            case CloseAccountResponseStatus.Rejected:
            case CloseAccountResponseStatus.Cancelled:
                if (msg.closeAccountRequestID === this.closeAccountRequestID) {
                    this.closeAccountRequestID = null;
                }
                break;
        }
        if (oldCloseAccountRequestID != this.closeAccountRequestID && this.Loaded) {
            this.OnChangeCloseAccountRequestID.Raise(msg);
        }
    }
    // Rule from Users\User groups->General->Service information added in #118235, logic in #119101
    EnableForceLinkingByAccount() {
        return this.isAllowedForMyUser(RulesSet.ACCOUNT_LINKED_WORKSPACE);
    }
    LoadAccountFollowerService() {
        if (this.isAllowedForMyUser(RulesSet.FUNCTION_COPY_TRADE)) {
            this.AccountFollowerService.onDataLoadingFinished();
        }
    }
    allAccountsIsOwnedByUser() {
        const accounts = this.getAccounts();
        let IsOwnedByUser = true;
        for (const accID in accounts) {
            const account = accounts[accID];
            IsOwnedByUser = IsOwnedByUser && account.IsOwnedByUser;
        }
        return IsOwnedByUser;
    }
    getUserSessionTimeout() {
        return this.getRuleNumberValueForAccount(RulesSet.USER_SESSION_TIMEOUT, this.MainAccountNew, -1);
    }
    processTradableIds(tradableIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const instrumentsPromises = tradableIds.map((element) => __awaiter(this, void 0, void 0, function* () { return yield this.getInstrumentByInstrumentTradableID_NFL(element, null, null, true); }));
            const instruments = yield Promise.all(instrumentsPromises);
            return instruments.filter((instrument) => instrument);
        });
    }
    updateInterestRate(message) {
        var _a;
        if (!message)
            return;
        const id = message.InterestsOnBalancePlanID;
        const plan = (_a = this.TryGetInterestPlan(id)) !== null && _a !== void 0 ? _a : new InterestPlan();
        plan.UpdateByMessage(message);
        this.InterestsOnBalancePlans.set(id, plan);
    }
    handleCustomInstrListForWatchList(customInstrList) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const trIDArray = (_a = customInstrList.tradableIds) !== null && _a !== void 0 ? _a : [];
            yield this.processTradableIds(trIDArray); // Waiting to receive instruments from a non-fixed list #123054
            const symbolIds = [];
            for (const trID of trIDArray) {
                const interiorIDsByTrID = this.getInstrumentsByTradableID(trID).map((instrument) => instrument.getInstrumentDescription());
                symbolIds.push(...interiorIDsByTrID);
            }
            return {
                name: this.customInstrumentListsCache.getLocalizedListName(customInstrList.id),
                instrDescriptions: symbolIds
            };
        });
    }
    LogTradingHaltWarning(instr) {
        const header = Resources.getResource('general.warnings.instrumentHaltHeader', [instr]);
        const message = Resources.getResource('general.warnings.instrumentHaltText', [instr]);
        EventCache.Add(new Event({
            Action: header,
            Description: [new EventDescriptionItem('', message)],
            EventType: EventType.Trading,
            EventSource: EventSource.In
        }));
    }
    getHomePageUrl() {
        if (!isNullOrUndefined(this.CompanyURL) && this.CompanyURL.trim() !== '') {
            return this.CompanyURL;
        }
        return Resources.getResource('homePage.link');
    }
}
export const DataCache = new _DataCache();
